import { FormBuilderConfig, Field, FieldOption } from '../models/form-builder.models';
import { UploadedFile } from '../../shared/lib/form/ni-file-upload/ni-file-upload.component';
import { DateUtils } from '../../shared/utils/date-utils';

/** Converts is_receiving_income to API format: "yes" | "no" (handles boolean true/false from forms) */
function toYesNo(value: unknown): string {
  if (value === true || value === 'true' || String(value).toLowerCase() === 'yes') return 'yes';
  if (value === false || value === 'false' || String(value).toLowerCase() === 'no') return 'no';
  return (value != null && value !== '') ? String(value) : '';
}

/** Same normalization as ProgramComponent: digits-only for matching SSN-style ids across formats. */
function normalizeIdentificationDigits(v: unknown): string {
  return String(v ?? '').replace(/\D/g, '');
}

export interface UtilityAssistancePayload {
  program_id: number;
  household_name: string;
  email_address: string;
  phone: string;
  identity_upload_key?: string[];
  citizenship_upload_key?: string[];
  proof_of_identity?: string; // Document types for identity files
  proof_of_citizenship?: string; // Document types for citizenship files
  upload_key?: string[]; // Legacy support
  is_apply_weatherization?: boolean | null;
  is_referred?: boolean | null;
  is_migrant_farm_worker?: string;
  is_disabled?: string;
  is_receiving_income?: string;
  address: Array<{
    address_type: string;
    address_line: string;
    apartment_number?: string;
    postal_code: string;
    city: string;
    state: string;
    country: string;
    county: string;
  }>;
  family_members: Array<{
    first_name: string;
    last_name: string;
    email: string;
    dob: string;
    gender: string;
    race: string;
    ethnicity: string;
    military_status: string;
    is_migrant_farm_worker: string;
    is_disabled: string;
    citizenship: string;
    is_receiving_income: string;
    identification_type: string;
    identification_number: string;
    age: string;
    reason_for_no_income?: string;
    identity_upload_key?: string[];
    citizenship_upload_key?: string[];
    proof_of_identity?: string; // Document types for identity files
    proof_of_citizenship?: string; // Document types for citizenship files
    is_declaration_accepted?: boolean;
    is_unemployed?: boolean;
    employed?: boolean;
  }>;
  income_details: Array<{
    email: string; // Empty for family members
    family_email?: string; // Only for family members
    is_family?: boolean; // Only for family members
    is_unemployed?: boolean; // For unemployed family members
    /** Unique per member when email is shared; match family_members.identification_number */
    identification_number?: string;
    income_type: string;
    income_source: string;
    frequency: string;
    income_amount1?: string;
    income_date1?: string;
    income_amount2?: string;
    income_date2?: string;
    income_amount3?: string;
    income_date3?: string;
    income_amount4?: string;
    income_date4?: string;
    income_amount5?: string;
    income_date5?: string;
    income_document: string[];
    upload_key: string[];
  }>;
  application_details: {
    household_type: string;
    own_or_rent_address: string;
    pays_rent: string;
    how_much?: string;
    payment_frequency?: string;
    apartment_or_landlord_name?: string;
    landlord_telephone?: string;
    landlord_alternate_telephone?: string;
    landlord_email?: string;
    service_utility: string;
    cool_home: string;
    heat_home: string;
    additionalProp1?: string;
    additionalProp2?: string;
    additionalProp3?: string;
  };
  service_details: Array<{
    bill_name: string;
    utility_company: string;
    account_number: string;
    bill_type: string;
    ia_bill_your_name: boolean;
    account_holder?: string;
    reason?: string;
    utility_bill_document: string;
    upload_key: string[];
  }>;
  applicant_sign: string;
  is_declaration_accepted: boolean;
  status: string;
  weatherization_details?: Record<string, any>;
}

export interface SeniorCenterProgramPayload {
  program_id: number;
  referral_source_type?: string;
  referral_source_type_specify_other?: string;
  household_name: string;
  email_address?: string;
  phone?: string;
  primary_contact_email?: string;
  identity_upload_key?: string[];
  citizenship_upload_key?: string[];
  proof_of_identity?: string;
  proof_of_citizenship?: string;
  // Prospect Contact Information
  first_name: string;
  last_name: string;
  dob: string;
  gender: string;
  gender_specify_other?: string;
  race: string;
  race_specify_other?: string;
  ethnicity: string;
  military_status: string;
  marital_status?: string;
  communication_preference?: string;
  communication_preference_specify_other?: string;
  is_migrant_farm_worker: string;
  is_disabled: string;
  citizenship: string;
  is_receiving_income: string;
  // Mobility Assistive Devices / Contact Information
  contact_information?: {
    phone?: string;
    email_address?: string;
    primary_contact_email?: string;
    lives_alone?: string;
    lives_with?: string;
    lives_with_specify_other?: string;
    place_of_residence?: string;
    place_of_residence_specify_other?: string;
    number_of_family_members?: string;
    approval_location_id?: string;
    approval_location_name?: string;
  };
  // Address Information
  address: Array<{
    address_type: string;
    address_line: string;
    apartment_number?: string;
    postal_code: string;
    city: string;
    state: string;
    country: string;
    county: string;
  }>;
  // Recipients (for Senior Center Program)
  recipients: Array<{
    recipient_name?: string;
    recipient_email?: string;
    name?: string;
    email?: string;
    first_name: string;
    last_name: string;
    middle_initial?: string;
    dob: string;
    sex: string;
    recipient_gender_specify_other?: string;
    race: string;
    recipient_race_specify_other?: string;
    ethnicity: string;
    marital_status?: string;
    age?: string;
    phone?: string;
    recipient_phone?: string;
    is_disabled?: string;
    is_receiving_income?: string;
    request_escort?: string;
    can_step_up_to_van?: string;
    mobility_assistive_devices?: string[];
    mobility_assistive_device_other_specify_other?: string;
    spouse?: {
      first_name?: string;
      last_name?: string;
      middle_initial?: string;
      dob?: string;
      sex?: string;
      spouse_gender_specify_other?: string;
      race?: string;
      spouse_race_specify_other?: string;
      ethnicity?: string;
      marital_status?: string;
      is_disabled?: string;
      is_receiving_income?: string;
      email?: string;
      phone?: string;
      ssn?: string;
    };
    health_plans?: Array<{
      insurance_program_type: string;
      insurance_program_type_specify_other?: string;
      id: string;
      id_response?: string;
      health_plan: string;
      health_plan_name_specify_other?: string;
    }>;
  }>;
  // Emergency Contact Information
  emergency_contacts: Array<{
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    contact_preference?: string;
    relationship?: string;
    emergency_contact_relationship_specify_other?: string;
    addresses?: Array<{
      address_type: string;
      address_line: string;
      apartment_number?: string;
      postal_code: string;
      city: string;
      state: string;
      country: string;
      county?: string;
    }>;
  }>;
  // Insurance Benefits
  health_plans: Array<{
    insurance_program_type: string;
    insurance_program_type_specify_other?: string;
    id: string;
    id_response?: string;
    health_plan: string;
    health_plan_name_specify_other?: string;
    upload_key?: string[];
  }>;
  // Nutritional Assessment
  nutritional_assessment?: Array<{
    recipient_name?: string;
    recipient_email?: string;
    nutrition_risk_questions: string[];
    total_score: number;
    risk_level: string;
  }>;
  // Monthly Household Income Information
  total_household_members?: string;
  total_household_income?: string | null;
  has_additional_monthly_household_income?: string;
  total_other_monthly_household_income?: string;
  income_details: Array<Record<string, any>>;
  // Client Preferences
  client_preferences?: {
    recipients: Array<{
      recipient_name?: string;
      recipient_email?: string;
      meal_type?: string[];
      drink_type?: string[];
      activities?: string[];
      activities_specify_other?: string;
      transportation_option?: string;
    }>;
  };
  client_rights_responsibilities?: {
    recipients: Array<{
      recipient_name?: string;
      recipient_email?: string;
    }>;
    referral_source_type?: string;
    referral_source_type_specify_other?: string;
    identification_proof?: string[];
    identification_proof_type?: string;
    /** Blob / storage keys for the uploaded identification document(s) (Senior Center); API expects a string list. */
    identification_proof_document?: string[];
    acknowledged_rights_responsibilities?: boolean;
    acknowledgement_date?: string;
    client_signature?: string;
  };
  code_of_conduct?: {
    recipients: Array<{
      recipient_name?: string;
      recipient_email?: string;
    }>;
    acknowledged_rules?: boolean;
    rules_read_to_me_acknowledged?: boolean;
    acknowledgement_date?: string;
    client_signature?: string;
  };
  photo_release?: {
    recipients: Array<{
      recipient_name?: string;
      recipient_email?: string;
    }>;
    acknowledged_photo_release?: boolean;
    refused_to_sign?: boolean;
    acknowledgement_date?: string;
    client_signature?: string;
  };
  physical_activity_waiver?: {
    recipients: Array<{
      recipient_name?: string;
      recipient_email?: string;
    }>;
    acknowledged_physical_activity_waiver?: boolean;
    acknowledgement_date?: string;
    client_signature?: string;
  };
  field_trip_agreement?: {
    recipients: Array<{
      recipient_name?: string;
      recipient_email?: string;
    }>;
    acknowledged_field_trip_agreement?: boolean;
    acknowledgement_date?: string;
    client_signature?: string;
  };
  signed_agreements?: {
    documents?: string[];
  };
  // Application Details (for other programs)
  application_details: {
    household_type: string;
    own_or_rent_address: string;
    pays_rent: string;
    how_much?: string | any[];
    payment_frequency?: string;
    apartment_or_landlord_name?: string;
    landlord_telephone?: string;
    landlord_alternate_telephone?: string;
    landlord_email?: string;
    service_utility: string;
    cool_home: string;
    heat_home: string;
  };
  // Family Members (if applicable)
  family_members?: any[];
  // Service Details (if applicable)
  service_details: any[];
  // Weatherization Details (if applicable)
  weatherization_details?: Record<string, any> | null;
  // Signatures and Declarations
  applicant_sign: string;
  is_declaration_accepted: boolean;
  applicant_acknowledgement?: boolean | null;
  acknowledgement_date?: string | null;
  release_of_information: boolean;
  /** Weatherization-specific waiver acknowledgement (Wx apply or Utility + 1084 Yes). */
  weatherization_waiver?: boolean | null;
  // Status
  status: string;
}

/**
 * Transform form values to Utility Assistance API payload
 * @param formValues - All form field values
 * @param config - Form configuration
 * @param uploadedFilesMap - Map of uploaded files (key: uniqueKey, value: UploadedFile[])
 * @param programId - Program ID from route or config
 * @param status - Submission status ('draft', 'pending', or 're-submit')
 */
export function transformUtilityAssistance(
  formValues: Record<string, any>,
  config: FormBuilderConfig | null,
  uploadedFilesMap: Record<string, UploadedFile[]>,
  programId: number = 1002,
  status: 'draft' | 'pending' | 're-submit' = 'pending',
  serviceList?: Array<{id: string | number, label: string, value: any}>
): UtilityAssistancePayload {
  
  // Helper to format date to YYYY-MM-DD (local calendar; no UTC shift)
  const formatDate = (date: any): string => DateUtils.toApiDateString(date);

  // Helper to calculate age from DOB
  const calculateAge = (dob: any): string => {
    if (!dob) return '';
    try {
      const birthDate = DateUtils.coerceToLocalDate(dob);
      if (!birthDate) return '';
      
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return String(age >= 0 ? age : '');
    } catch {
      return '';
    }
  };

  // Helper to get upload keys from form value or uploadedFilesMap
  // IMPORTANT: This function must ONLY return keys from the specified groupId to prevent mixing household and family member uploads
  const getUploadKeys = (fieldId: string, uniqueId?: string, groupId?: string, sectionId: string = 'application_upload'): string[] => {
    // For table_accordion files (with uniqueId) - family member uploads
    if (uniqueId && groupId) {
      const uniqueKey = `${sectionId}_${groupId}_${fieldId}_${uniqueId}`;
      const uploadedFiles = uploadedFilesMap[uniqueKey] || [];
      const keys = uploadedFiles
        .filter(uf => uf.key && !uf.uploading && !uf.error)
        .map(uf => uf.key!);
      
      return keys;
    }
    
    // Regular field upload (household level) - ONLY for group 1009
    // CRITICAL: For root level uploads (group 1009), ONLY use uploadedFilesMap to prevent mixing with family member files
    // Do NOT check form values as they might contain mixed files from different groups
    if (!groupId) {
      
      return [];
    }
    
    // For household uploads (group 1009), check uploadedFilesMap first, then form values as fallback
    const regularKey = `${sectionId}_${groupId}_${fieldId}`;
    const uploadedFiles = uploadedFilesMap[regularKey] || [];
    let keys = uploadedFiles
      .filter(uf => uf.key && !uf.uploading && !uf.error)
      .map(uf => uf.key!);
    
    // For group 1009 (household), also check form values as fallback if no files found in uploadedFilesMap
    // This handles cases where files might be stored in form values
    if (groupId === '1009' && keys.length === 0) {
      const value = formValues[fieldId];
      if (value) {
        if (Array.isArray(value)) {
          keys = value.filter(v => v && typeof v === 'string');
        } else if (typeof value === 'string') {
          keys = value.split(',').filter(k => k.trim());
        }
      }
    }
    
    return keys;
  };

  // Helper function to collect all family member upload keys
  const collectFamilyMemberKeys = (uploadedFilesMap: Record<string, UploadedFile[]>): Set<string> => {
  const allFamilyMemberKeys = new Set<string>();
  
  Object.keys(uploadedFilesMap).forEach(mapKey => {
    if (mapKey.startsWith('application_upload_1010_1054_') || mapKey.startsWith('application_upload_1010_1108_')) {
      const files = uploadedFilesMap[mapKey] || [];
      files.forEach(file => {
        if (file.key && !file.uploading && !file.error) {
          allFamilyMemberKeys.add(file.key);
        }
      });
    }
  });
  
    return allFamilyMemberKeys;
  };

  // Helper function to check if key exists in form value
  const isKeyInFormValue = (formValue: any, key: string): boolean => {
    if (!formValue) return false;
    
    if (Array.isArray(formValue)) {
      return formValue.includes(key);
    }
    
    if (typeof formValue === 'string') {
      return formValue.split(',').map(k => k.trim()).includes(key);
    }
    
    return false;
  };

  // Helper function to filter and verify upload keys belong to household only
  const filterHouseholdKeys = (rawKeys: string[], fieldId: string, allFamilyMemberKeys: Set<string>, uploadedFilesMap: Record<string, UploadedFile[]>, formValues: Record<string, any>): string[] => {
    const verifiedKeys = rawKeys.filter(key => {
      if (allFamilyMemberKeys.has(key)) {
        return false;
      }
      
      const householdMapKey = `application_upload_1009_${fieldId}`;
      const householdFiles = uploadedFilesMap[householdMapKey] || [];
      if (householdFiles.some(uf => uf.key === key && !uf.uploading && !uf.error)) {
        return true;
      }
      
      return isKeyInFormValue(formValues[fieldId], key);
    });
    
    return [...new Set(verifiedKeys)];
  };

  // Helper function to get document type from form values for a field
  // For primary applicant: uses fieldId_document_type (e.g., 1054_document_type)
  // For family members: uses fieldId_document_type_uniqueId (e.g., 1054_document_type_member_0_1234567890)
  const getDocumentTypeFromForm = (fieldId: string, formValues: Record<string, any>, uniqueId?: string): string | undefined => {
    if (uniqueId) {
      // For family members, look for accordion-specific field ID
      const accordionDocumentTypeFieldId = `${fieldId}_document_type_${uniqueId}`;
      if (formValues[accordionDocumentTypeFieldId]) {
        return formValues[accordionDocumentTypeFieldId];
      }
    }
    // For primary applicant, use regular field ID
    const documentTypeFieldId = `${fieldId}_document_type`;
    return formValues[documentTypeFieldId] || undefined;
  };

  // Helper function to extract document types from uploadedFilesMap for given keys
  // Falls back to form value if documentType not found in uploadedFilesMap
  // For family members, uniqueId should be provided to get the correct accordion-specific document type
  const getDocumentTypesForKeys = (keys: string[], uploadedFilesMap: Record<string, UploadedFile[]>, fieldId: string, formValues: Record<string, any>, uniqueId?: string): string[] => {
    const documentTypes: string[] = [];
    // Get document type from form - this is the primary source (user's current selection)
    const defaultDocumentType = getDocumentTypeFromForm(fieldId, formValues, uniqueId);
    
    // If we have a document type from the form, use it for all keys
    if (defaultDocumentType && keys.length > 0) {
      // Return the form value as it represents the user's current selection
      return [defaultDocumentType];
    }
    
    // Fallback: try to find document type from uploadedFilesMap (for legacy data)
    for (const key of keys) {
      let foundType: string | undefined = undefined;
      
      // Try to find document type from uploadedFilesMap
      for (const [mapKey, files] of Object.entries(uploadedFilesMap)) {
        const file = files.find(f => f.key === key);
        if (file && file.documentType) {
          foundType = file.documentType;
          break;
        }
      }
      
      if (foundType) {
        documentTypes.push(foundType);
      }
    }
    return documentTypes;
  };

  // 1. Root level upload keys - split into identity_upload_key (1054) and citizenship_upload_key (1108)
  const allFamilyMemberKeys = collectFamilyMemberKeys(uploadedFilesMap);
  const identityUploadKeys = getUploadKeys('1054', undefined, '1009', 'application_upload');
  const citizenshipUploadKeys = getUploadKeys('1108', undefined, '1009', 'application_upload');
  const verifiedIdentityKeys = filterHouseholdKeys(identityUploadKeys, '1054', allFamilyMemberKeys, uploadedFilesMap, formValues);
  const verifiedCitizenshipKeys = filterHouseholdKeys(citizenshipUploadKeys, '1108', allFamilyMemberKeys, uploadedFilesMap, formValues);
  
  // Extract document types for root level files
  const rootProofOfIdentity = getDocumentTypesForKeys(verifiedIdentityKeys, uploadedFilesMap, '1054', formValues);
  const rootProofOfCitizenship = getDocumentTypesForKeys(verifiedCitizenshipKeys, uploadedFilesMap, '1108', formValues);

  // Helper function to build addresses array
  const buildAddresses = (formValues: Record<string, any>): any[] => {
  const addresses: any[] = [];
  
  if (formValues['1007'] || formValues['1008'] || formValues['1009']) {
    addresses.push({
      address_type: formValues['1060'] || 'residential',
      address_line: formValues['1007'] || '',
      apartment_number: formValues['1092'] || '',
      postal_code: formValues['1008'] || '',
      city: formValues['1009'] || '',
      state: formValues['1010'] || '',
      country: formValues['1011'] || '',
      county: formValues['1091'] || ''
    });
  }

  const hasMailingAddress = formValues['1059'] === true || 
                           (Array.isArray(formValues['1059']) && formValues['1059'].length > 0) ||
                           formValues['1059'] === 'true';
  
  if (hasMailingAddress && (formValues['1061'] || formValues['1062'] || formValues['1063'])) {
    addresses.push({
      address_type: formValues['1066'] || 'mailing',
      address_line: formValues['1061'] || '',
      apartment_number: formValues['1109'] || '',
      postal_code: formValues['1062'] || '',
      city: formValues['1063'] || '',
      state: formValues['1064'] || '',
      country: formValues['1065'] || '',
      county: formValues['1083'] || ''
    });
  }

    return addresses;
  };

  // 2. Address array (residential + mailing)
  const addresses = buildAddresses(formValues);

  // Check if this is Senior Center program (1000) - skip family members, income, and service details processing
  const isSeniorCenterProgram = programId === 1000;
  
  // 3. Family members array with upload_key per member (skip for Senior Center program)
  const familyMembers: any[] = [];
  if (!isSeniorCenterProgram) {
  const tableData = formValues['table_1004'] || [];
  // Primary contact (householder) - exclude from family_members payload since they're already in Personal Information
  const primaryContactEmail = (formValues['1005'] || formValues['contact_information_email'] || formValues['contact_email_address'] || formValues['email_address'] || '').trim().toLowerCase();
  const primaryFirstName = (formValues['contact_information_first_name'] || formValues['first_name'] || '').trim().toLowerCase();
  const primaryLastName = (formValues['contact_information_last_name'] || formValues['last_name'] || '').trim().toLowerCase();

  // Get all unique IDs from application_upload section group 1010 data object
  // This helps us match family members to their uploads even if unique ID doesn't match
  let applicationUploadGroupData: Record<string, any> = {};
  if (config) {
    const uploadSection = config.sections?.find(s => s.section_id === 'application_upload');
    const tableAccordionGroup = uploadSection?.groups?.find(g => g.group_id === '1010');
    if (tableAccordionGroup && (tableAccordionGroup as any).data) {
      applicationUploadGroupData = (tableAccordionGroup as any).data;
    }
  }
  
    // Helper functions for processing family members
    const extractKeysFromTableData = (fieldValue: any, fieldId: string, index: number, firstName: string, lastName: string): string[] => {
      if (fieldValue === undefined || fieldValue === null) {
        return [];
      }
      
      if (Array.isArray(fieldValue)) {
        const keys = fieldValue.filter((v: any) => v && typeof v === 'string');
        console.log(`[transformUtilityAssistance] Member ${index} (${firstName} ${lastName}) - ${fieldId === '1054' ? 'Identity' : 'Citizenship'} keys from table data:`, keys);
        return keys;
      }
      
      if (typeof fieldValue === 'string') {
        const keys = fieldValue.split(',').filter((k: string) => k.trim());
        console.log(`[transformUtilityAssistance] Member ${index} (${firstName} ${lastName}) - ${fieldId === '1054' ? 'Identity' : 'Citizenship'} keys from table data (string):`, keys);
        return keys;
      }
      
      const keyStr = String(fieldValue || '').trim();
        if (keyStr) {
        const keys = [keyStr];
        console.log(`[transformUtilityAssistance] Member ${index} (${firstName} ${lastName}) - ${fieldId === '1054' ? 'Identity' : 'Citizenship'} keys from table data (converted):`, keys);
        return keys;
      }
      
      return [];
    };

    const tryFindFilesByNameMatch = (fieldId: string, memberFullName: string, applicationUploadGroupData: Record<string, any>): string[] => {
      if (Object.keys(applicationUploadGroupData).length === 0) {
        return [];
      }
      
          for (const [dataUniqueId, dataValue] of Object.entries(applicationUploadGroupData)) {
            const dataLabel = dataValue?.label ?? '';
            if (dataLabel && memberFullName && dataLabel.toLowerCase().includes(memberFullName.toLowerCase())) {
          const keys = getUploadKeys(fieldId, dataUniqueId, '1010', 'application_upload');
          if (keys.length > 0) {
            return keys;
          }
        }
      }
      
      return [];
    };

    const getIdentityKeysWithFallback = (member: any, memberUniqueId: string, index: number, firstName: string, lastName: string, applicationUploadGroupData: Record<string, any>): string[] => {
      const hasField1054 = member['1054'] !== undefined && member['1054'] !== null;
      let keys = extractKeysFromTableData(member['1054'], '1054', index, firstName, lastName);
      
      if (memberUniqueId && !hasField1054) {
        console.log(`[transformUtilityAssistance] Member ${index} (${firstName} ${lastName}) - Field 1054 missing, using fallback for uniqueId: ${memberUniqueId}`);
        keys = getUploadKeys('1054', memberUniqueId, '1010', 'application_upload');
        
        if (keys.length === 0) {
          const memberFullName = `${firstName} ${lastName}`.trim();
          keys = tryFindFilesByNameMatch('1054', memberFullName, applicationUploadGroupData);
        }
        
        if (keys.length === 0) {
          console.warn(`[transformUtilityAssistance] ⚠️ Member ${index} (${firstName} ${lastName}) - No identity files found even after fallback strategies. Field 1054 is missing from table data.`);
        }
      }
      
      return keys;
    };

    const getCitizenshipKeysWithFallback = (member: any, memberUniqueId: string, index: number, firstName: string, lastName: string, applicationUploadGroupData: Record<string, any>): string[] => {
      const hasField1108 = member['1108'] !== undefined && member['1108'] !== null;
      let keys = extractKeysFromTableData(member['1108'], '1108', index, firstName, lastName);
      
      if (memberUniqueId && !hasField1108) {
        console.log(`[transformUtilityAssistance] Member ${index} (${firstName} ${lastName}) - Field 1108 missing, using fallback for uniqueId: ${memberUniqueId}`);
        keys = getUploadKeys('1108', memberUniqueId, '1010', 'application_upload');
        
        if (keys.length === 0) {
          const memberFullName = `${firstName} ${lastName}`.trim();
          keys = tryFindFilesByNameMatch('1108', memberFullName, applicationUploadGroupData);
          if (keys.length > 0) {
            console.log(`[transformUtilityAssistance] Member ${index} (${firstName} ${lastName}) - Found citizenship files by name match:`, keys);
          }
        } else {
          console.log(`[transformUtilityAssistance] Member ${index} (${firstName} ${lastName}) - Found citizenship files from uploadedFilesMap:`, keys);
        }
        
        if (keys.length === 0) {
          console.warn(`[transformUtilityAssistance] ⚠️ Member ${index} (${firstName} ${lastName}) - No citizenship files found even after fallback strategies. Field 1108 is missing from table data.`);
        }
      }
      
      return keys;
    };

    // Helper function to check if declaration checkbox is checked
    const isDeclarationCheckboxChecked = (): boolean => {
      // Check field 1112 (Declaration of Income Statement checkbox)
      const field1112 = formValues['1112'];
      if (field1112 !== undefined) {
        if (Array.isArray(field1112)) {
          return field1112.length > 0 && (field1112.includes('true') || field1112.includes(true));
        }
        return field1112 === true || field1112 === 'true';
      }
      
      // Fallback to field 1057 if 1112 is not available
      const field1057 = formValues['1057'];
      if (field1057 !== undefined) {
        if (Array.isArray(field1057)) {
          return field1057.length > 0 && (field1057.includes('true') || field1057.includes(true));
        }
        return field1057 === true || field1057 === 'true';
      }
      
      return false;
    };

    // Helper function to check if a member is unemployed and 18+
    const isUnemployedAnd18Plus = (member: any): boolean => {
      // Check if unemployed (is_receiving_income is 'unemployed', 'no', false, or 'No')
      const isReceivingIncome = member['1104'];
      const isUnemployed = isReceivingIncome === 'unemployed' ||
                          isReceivingIncome === false || 
                          isReceivingIncome === 'no' || 
                          isReceivingIncome === 'No' || 
                          String(isReceivingIncome).toLowerCase().trim() === 'no';
      
      if (!isUnemployed) {
        return false;
      }
      
      // Check age: must be 18 or older
      let age: number | null = null;
      
      // Try to get age from stored age field (1080)
      const storedAge = member['1080'];
      if (storedAge !== undefined && storedAge !== null && storedAge !== '') {
        const ageNum = parseInt(String(storedAge), 10);
        if (!isNaN(ageNum)) {
          age = ageNum;
        }
      }
      
      // If age not found, calculate from DOB
      if (age === null) {
        const dob = member['1096'];
        if (dob) {
          const calculatedAge = calculateAge(dob);
          if (calculatedAge) {
            const ageNum = parseInt(calculatedAge, 10);
            if (!isNaN(ageNum)) {
              age = ageNum;
            }
          }
        }
      }
      
      // Return true if age is 18 or older
      return age !== null && age >= 18;
    };

    const buildFamilyMemberObject = (member: any, uniqueMemberUploadKeys: string[], uniqueMemberCitizenshipUploadKeys: string[], memberUniqueId: string): any => {
      // Check if member is unemployed or employed
      const isReceivingIncome = member['1104'];
      const isUnemployed = isReceivingIncome === 'unemployed' || 
                          isReceivingIncome === false || 
                          isReceivingIncome === 'no' || 
                          String(isReceivingIncome).toLowerCase().trim() === 'no';
      
      const isEmployed = isReceivingIncome === 'employed' ||
                        isReceivingIncome === true ||
                        isReceivingIncome === 'yes' ||
                        String(isReceivingIncome).toLowerCase().trim() === 'yes' ||
                        isReceivingIncome === 'true';
      
      // Extract document types for this member's files
      // For family members, we need to get document type from the accordion-specific form control
      // The form control ID is: fieldId_document_type_uniqueId (e.g., 1054_document_type_member_0_1234567890)
      const memberProofOfIdentity = getDocumentTypesForKeys(uniqueMemberUploadKeys, uploadedFilesMap, '1054', formValues, memberUniqueId);
      const memberProofOfCitizenship = getDocumentTypesForKeys(uniqueMemberCitizenshipUploadKeys, uploadedFilesMap, '1108', formValues, memberUniqueId);
      
      const familyMember: any = {
      first_name: member['1094'] || '',
      last_name: member['1095'] || '',
      email: member['1097'] || '',
      dob: formatDate(member['1096']),
      gender: member['1098'] || '',
      race: member['1099'] || '',
      ethnicity: member['1100'] || '',
      military_status: member['1101'] || '',
      is_migrant_farm_worker: member['1102'] || '',
      is_disabled: member['1105'] || '',
      citizenship: member['1103'] || '',
      is_receiving_income: toYesNo(member['1104']) || '',
      identification_type: member['1078'] || '',
      identification_number: member['1079'] || '',
      age: member['1080'] || calculateAge(member['1096']),
        identity_upload_key: uniqueMemberUploadKeys,
        citizenship_upload_key: uniqueMemberCitizenshipUploadKeys,
        proof_of_identity: memberProofOfIdentity.length > 0 ? memberProofOfIdentity[0] : undefined,
        proof_of_citizenship: memberProofOfCitizenship.length > 0 ? memberProofOfCitizenship[0] : undefined,
      };
      
      // Add is_unemployed=true if member is unemployed
      if (isUnemployed) {
        familyMember.is_unemployed = true;
      }
      
      // Add employed=true if member is employed
      if (isEmployed) {
        familyMember.employed = true;
      }
      
      // Add reason_for_no_income if provided (field 1106)
      if (member['1106'] !== undefined && member['1106'] !== null && member['1106'] !== '') {
        familyMember.reason_for_no_income = member['1106'];
      }
      
      // Add is_declaration_accepted only if:
      // 1. Member is unemployed and 18+
      // 2. Declaration checkbox (field 1112 or 1057) is checked
      if (isUnemployedAnd18Plus(member) && isDeclarationCheckboxChecked()) {
        familyMember.is_declaration_accepted = true;
      }
      
      return familyMember;
    };

    tableData.forEach((member: any, index: number) => {
      const memberUniqueId = member['1107'] || member['x`'];
      const firstName = member['1094'] || '';
      const lastName = member['1095'] || '';
      const memberEmail = (member['1097'] || '').trim().toLowerCase();
      const memberFirstName = (firstName || '').trim().toLowerCase();
      const memberLastName = (lastName || '').trim().toLowerCase();

      // Skip primary contact - their data is already in Personal Information; exclude from family_members payload
      const isPrimaryByEmail = primaryContactEmail && memberEmail && memberEmail === primaryContactEmail;
      const isPrimaryByName = primaryFirstName && primaryLastName && memberFirstName === primaryFirstName && memberLastName === primaryLastName;
      if (isPrimaryByEmail || isPrimaryByName) {
        console.log(`[transformUtilityAssistance] Skipping primary contact from family_members: ${firstName} ${lastName} (${memberEmail})`);
        return;
      }

      console.log(`[transformUtilityAssistance] Processing member ${index}:`, {
        uniqueId: memberUniqueId,
        firstName,
        lastName,
        '1054': member['1054'],
        '1108': member['1108']
      });

      const memberUploadKeys = getIdentityKeysWithFallback(member, memberUniqueId, index, firstName, lastName, applicationUploadGroupData);
      const memberCitizenshipUploadKeys = getCitizenshipKeysWithFallback(member, memberUniqueId, index, firstName, lastName, applicationUploadGroupData);

      const uniqueMemberUploadKeys = [...new Set(memberUploadKeys)];
      const uniqueMemberCitizenshipUploadKeys = [...new Set(memberCitizenshipUploadKeys)];

      console.log(`[transformUtilityAssistance] ✅ Final keys for member ${index} (${firstName} ${lastName}, uniqueId: ${memberUniqueId}):`, {
        identity_upload_key: uniqueMemberUploadKeys,
        citizenship_upload_key: uniqueMemberCitizenshipUploadKeys,
        '1054_from_table': member['1054'],
        '1108_from_table': member['1108']
      });

      // Pass memberUniqueId to buildFamilyMemberObject so it can get the correct document types from accordion-specific form controls
      const familyMember = buildFamilyMemberObject(member, uniqueMemberUploadKeys, uniqueMemberCitizenshipUploadKeys, memberUniqueId);
      familyMembers.push(familyMember);
    });

    // Include all family members in the payload (both employed and unemployed) - primary contact excluded
    console.log(`[transformUtilityAssistance] Added ${familyMembers.length} family members to payload`);
  }

  // Get householder email early - used for both income details and final payload
  // Priority: formValues['1005'] (direct field) > email_address (from formValues) > empty string
  const householderEmail = (formValues['1005'] || formValues['email_address'] || '').trim();

  // 4. Income details with upload keys (skip for Senior Center program)
  const incomeDetails: any[] = [];
  if (!isSeniorCenterProgram) {
    
    // Map uniqueId -> email; normalized identification digits -> uniqueId (members may share email)
    const familyMemberEmailMap: Record<string, string> = {};
    const familyTableDataForEmailMap = formValues['table_1004'] || [];
      familyTableDataForEmailMap.forEach((member: any) => {
      // Try multiple possible uniqueId fields
      const uniqueId = member['1107'] || member['uniqueId'] || member['x`'] || '';
      const email = member['1097'] || '';
      if (uniqueId && email) {
        familyMemberEmailMap[uniqueId] = email;
        familyMemberEmailMap[String(uniqueId)] = email;
      }
    });
    
    // Helper function to check if income entry has meaningful data
    const hasIncomeData = (income: any): boolean => {
      // Check if any income fields have values
      const hasIncomeType = income['1068'] && String(income['1068']).trim() !== '';
      const hasIncomeSource = income['1093'] && String(income['1093']).trim() !== '';
      const hasFrequency = income['1069'] && String(income['1069']).trim() !== '';
      
      // Check for amount fields (1070, 1070_1, 1070_2, etc.)
      let hasAmount = false;
      for (let i = 0; i <= 5; i++) {
        const amountKey = i === 0 ? '1070' : `1070_${i}`;
        if (income[amountKey] && String(income[amountKey]).trim() !== '') {
          hasAmount = true;
          break;
        }
      }
      
      // Check for date fields (1071, 1071_1, 1071_2, etc.)
      let hasDate = false;
      for (let i = 0; i <= 5; i++) {
        const dateKey = i === 0 ? '1071' : `1071_${i}`;
        if (income[dateKey] && String(income[dateKey]).trim() !== '') {
          hasDate = true;
          break;
        }
      }
      
      const hasUploadKey = income['1072'] && (
        (Array.isArray(income['1072']) && income['1072'].length > 0) ||
        (typeof income['1072'] === 'string' && income['1072'].trim() !== '')
      );
      
      // Return true only if at least one meaningful field has data
      // Must have at least income_type OR (income_source AND frequency) OR amount/date
      // Upload key alone is NOT sufficient - must have actual income data
      return hasIncomeType || (hasIncomeSource && hasFrequency) || hasAmount || hasDate;
    };
    
    // Helper function to build income entry
    const buildIncomeEntry = (
      income: any,
      email: string,
      familyEmail?: string,
      isFamily?: boolean,
      identificationNumber?: string
    ): any => {
      // Get upload keys from field 1072 (file upload in income table)
      let incomeUploadKeys: string[] = [];
      
      // Try to get from form value first
      if (income['1072']) {
        if (Array.isArray(income['1072'])) {
          incomeUploadKeys = income['1072'].filter((v: any) => v && typeof v === 'string');
        } else if (typeof income['1072'] === 'string') {
          incomeUploadKeys = income['1072'].split(',').filter((k: string) => k.trim());
        }
      }
      
      // Build income entry object
      // Use familyEmail for email field if it's a family member, otherwise use the provided email
      const finalEmail = (isFamily && familyEmail) ? familyEmail : email;
      
      // Second detailed Income Source (1093) removed from Add Income UI;
      // fall back to high-level type (1068) so API income_source is still populated.
      const incomeType = String(income['1068'] ?? '').trim();
      const incomeSource = String(income['1093'] ?? '').trim() || incomeType;

      const incomeEntry: any = {
        email: finalEmail, // Use family member's own email for family members, householder email for householder
        income_type: incomeType,
        income_source: incomeSource,
        frequency: income['1069'] || '',
        income_document: income['1072'] ? (Array.isArray(income['1072']) ? income['1072'] : [income['1072']]) : [],
        upload_key: incomeUploadKeys
      };
      
      // Add family_email and is_family for family members
      if (isFamily && familyEmail) {
        incomeEntry.family_email = familyEmail;
        incomeEntry.is_family = true;
      }
      if (isFamily && identificationNumber !== undefined && identificationNumber !== '') {
        incomeEntry.identification_number = identificationNumber;
      }
      
      // Extract and map income pairs - ALL pairs are numbered (1-5)
      // First pair (1070, 1071) maps to income_amount1, income_date1
      // Second pair (1070_1, 1071_1) maps to income_amount2, income_date2
      // And so on...
      
      // Collect all pairs in order
      const incomePairs: Array<{amount: any, date: any}> = [];
      
      // First pair: base fields (1070, 1071)
      if (income['1070'] || income['1071']) {
        incomePairs.push({
          amount: income['1070'] || '',
          date: income['1071'] || ''
        });
      }
      
      // Additional pairs: numbered fields (1070_1, 1071_1, 1070_2, 1071_2, etc.)
      for (let i = 1; i <= 5; i++) {
        const amountKey = `1070_${i}`;
        const dateKey = `1071_${i}`;
        
        // Only include if at least one field has a value
        if (income[amountKey] || income[dateKey]) {
          incomePairs.push({
            amount: income[amountKey] || '',
            date: income[dateKey] || ''
          });
        }
      }
      
      // Map all pairs to numbered API fields (income_amount1-date1 through income_amount5-date5)
      incomePairs.forEach((pair, index) => {
        const pairNumber = index + 1; // 1-based numbering (1, 2, 3, 4, 5)
        if (pairNumber <= 5) {
          incomeEntry[`income_amount${pairNumber}`] = pair.amount || '';
          incomeEntry[`income_date${pairNumber}`] = pair.date ? formatDate(pair.date) : '';
        }
      });
      
      return incomeEntry;
    };
    
    // Process householder income (from table_1005 or table_1006_householder)
    // Only process if householder email exists
    if (householderEmail && householderEmail.trim() !== '') {
      const householderIncomeData = formValues['table_1005'] || formValues['table_1006_householder'] || [];
      let hasHouseholderIncomeData = false;
      
      if (Array.isArray(householderIncomeData)) {
        householderIncomeData.forEach((income: any) => {
          // Only add income entry if it has meaningful data
          if (hasIncomeData(income)) {
            const incomeEntry = buildIncomeEntry(income, householderEmail);
            // Ensure householder entries don't have family flags
            incomeEntry.is_family = false;
            delete incomeEntry.family_email;
            incomeDetails.push(incomeEntry);
            hasHouseholderIncomeData = true;
          }
        });
      }
      
      // Check if householder is unemployed (field 1104 = "no")
      // This should be checked regardless of whether there's income data, 
      // because unemployed householder should have an entry even if no income table data exists
      const householderData = formValues['table_1000'] || formValues['table_1001'] || [];
      let isUnemployed = false;
      let isReceivingIncome: any = null;
      
      if (householderData.length > 0) {
        const householder = householderData[0];
        isReceivingIncome = householder['1104'] || householder['is_receiving_income'];
        isUnemployed = isReceivingIncome === 'no' || 
                      isReceivingIncome === 'No' || 
                      String(isReceivingIncome).toLowerCase().trim() === 'no' ||
                      isReceivingIncome === false;
      } else {
        // Check form values directly
        isReceivingIncome = formValues['1104'] || formValues['1021'];
        isUnemployed = isReceivingIncome === 'no' || 
                      isReceivingIncome === 'No' || 
                      String(isReceivingIncome).toLowerCase().trim() === 'no' ||
                      isReceivingIncome === false;
      }
      
      console.log(`[transformUtilityAssistance] Householder income check:`, {
        householderEmail,
        hasHouseholderIncomeData,
        isUnemployed,
        isReceivingIncome,
        householderDataLength: householderData.length
      });
      
      // If householder is unemployed and has no income data, add unemployed entry
      if (isUnemployed && !hasHouseholderIncomeData) {
        // Get wage income inquiry upload keys from field 1113 for householder
        // Try multiple possible key formats
        const possibleKeys = [
          'income_details_1005_1113',
          'income_details_1005_1113_0',
          '1005_1113',
          '1113'
        ];
        
        let wageInquiryUploadKeys: string[] = [];
        let uploadedWageFiles: UploadedFile[] = [];
        
        // Check form values for each possible key
        for (const key of possibleKeys) {
          const value = formValues[key];
          if (value) {
            if (Array.isArray(value)) {
              wageInquiryUploadKeys = [...wageInquiryUploadKeys, ...value.filter((v: any) => v && typeof v === 'string')];
            } else if (typeof value === 'string') {
              wageInquiryUploadKeys = [...wageInquiryUploadKeys, ...value.split(',').filter((k: string) => k.trim())];
            }
          }
          
          // Check uploadedFilesMap for each possible key
          if (uploadedFilesMap[key] && Array.isArray(uploadedFilesMap[key])) {
            uploadedWageFiles = [...uploadedWageFiles, ...uploadedFilesMap[key]];
          }
        }
        
        // Extract upload keys from uploadedFilesMap
        const mapUploadKeys = uploadedWageFiles
          .filter(uf => uf.key && !uf.uploading && !uf.error)
          .map(uf => uf.key!);
        
        // Merge both sources, remove duplicates
        const allWageInquiryKeys = [...new Set([...wageInquiryUploadKeys, ...mapUploadKeys])];
        
        console.log(`[transformUtilityAssistance] Creating income entry for unemployed householder ${householderEmail}`, {
          wageInquiryKeys: allWageInquiryKeys,
          formValueKeys: wageInquiryUploadKeys,
          mapKeys: mapUploadKeys,
          possibleKeysChecked: possibleKeys,
          uploadedFilesMapKeys: Object.keys(uploadedFilesMap).filter(k => k.includes('1113') || k.includes('1005'))
        });
        
        // Create income entry for unemployed householder with wage income inquiry upload
        // Add entry even if no upload keys found (API might handle it differently)
        const unemployedIncomeEntry: any = {
          email: householderEmail,
          income_type: '',
          income_source: '',
          frequency: '',
          income_document: [],
          upload_key: allWageInquiryKeys.length > 0 ? allWageInquiryKeys : [],
          is_unemployed: true,
          is_family: false
        };
        
        console.log(`[transformUtilityAssistance] Pushing unemployed householder income entry:`, unemployedIncomeEntry);
        incomeDetails.push(unemployedIncomeEntry);
      }
    }
    
    // Process family member income - iterate through family members first to ensure we don't miss any
    // This approach is more reliable than looking for table keys first
    familyTableDataForEmailMap.forEach((member: any) => {
      const uniqueId = member['1107'] || member['uniqueId'] || member['x`'] || '';
      const email = member['1097'] || '';
      
      // Skip if this is actually the householder or email is missing
      if (!email || email.trim() === '' || email.trim() === householderEmail.trim()) {
        return;
      }
      
      // Try multiple possible table key formats
      const possibleTableKeys = uniqueId ? [
        `table_1006_${uniqueId}`,
        `table_1006_${String(uniqueId)}`
      ] : [];
      
      let familyIncomeData: any[] | null = null;
      let matchedKey: string | null = null;
      
      // Income rows are scoped by uniqueId only. Do not fall back to "first table matching email"
      // when multiple members share an email — that mis-assigns rows.
      for (const tableKey of possibleTableKeys) {
        const tableData = formValues[tableKey];
        if (tableData && Array.isArray(tableData) && tableData.length > 0) {
          familyIncomeData = tableData;
          matchedKey = tableKey;
          break;
        }
      }
      
      // Check if member is unemployed (they might not have income table data)
      const isReceivingIncome = member['1104'];
      const isUnemployed = isReceivingIncome === 'unemployed' || 
                          isReceivingIncome === false || 
                          isReceivingIncome === 'no' || 
                          String(isReceivingIncome).toLowerCase().trim() === 'no';
      
      // Process income data if found
      if (familyIncomeData && Array.isArray(familyIncomeData) && familyIncomeData.length > 0) {
        console.log(`[transformUtilityAssistance] Processing income for family member ${email} (uniqueId: ${uniqueId}), table key: ${matchedKey}, entries: ${familyIncomeData.length}`);
        
        familyIncomeData.forEach((income: any, index: number) => {
          console.log(`[transformUtilityAssistance] Processing income entry ${index} for ${email}:`, {
            hasIncomeType: !!income['1068'],
            hasIncomeSource: !!income['1093'],
            hasFrequency: !!income['1069'],
            hasAmount: !!(income['1070'] || income['1070_1']),
            hasData: hasIncomeData(income)
          });
          
          // Only add income entry if it has meaningful data
          if (hasIncomeData(income)) {
            const memberIdentification = member['1079'] || '';
            const incomeEntry = buildIncomeEntry(income, '', email, true, memberIdentification);
            // Ensure family member entries have correct email and flags
            incomeEntry.email = email; // Use family member's own email
            incomeEntry.family_email = email;
            incomeEntry.is_family = true;
            
            // If member is unemployed, add is_unemployed flag and check for field 1113 upload keys
            if (isUnemployed) {
              incomeEntry.is_unemployed = true;
              
              // Get wage income inquiry upload keys from field 1113
              const familyWageInquiryKey = `income_details_1006_1113_${uniqueId}`;
              const familyWageInquiry = formValues[familyWageInquiryKey];
              
              let wageInquiryUploadKeys: string[] = [];
              if (familyWageInquiry) {
                if (Array.isArray(familyWageInquiry)) {
                  wageInquiryUploadKeys = familyWageInquiry.filter((v: any) => v && typeof v === 'string');
                } else if (typeof familyWageInquiry === 'string') {
                  wageInquiryUploadKeys = familyWageInquiry.split(',').filter((k: string) => k.trim());
                }
              }
              
              // Also check uploadedFilesMap for wage income inquiry files
              const wageInquiryMapKey = `income_details_1006_1113_${uniqueId}`;
              const uploadedWageFiles = uploadedFilesMap[wageInquiryMapKey] || [];
              const mapUploadKeys = uploadedWageFiles
                .filter(uf => uf.key && !uf.uploading && !uf.error)
                .map(uf => uf.key!);
              
              // Merge both sources, remove duplicates
              const allWageInquiryKeys = [...new Set([...wageInquiryUploadKeys, ...mapUploadKeys])];
              
              // For unemployed members, use field 1113 upload keys (wage income inquiry) instead of field 1072
              // Merge with existing upload_key from field 1072 if any, but prioritize field 1113
              if (allWageInquiryKeys.length > 0) {
                // Use wage inquiry keys as primary, merge with any existing 1072 keys
                const mergedKeys = [...new Set([...allWageInquiryKeys, ...incomeEntry.upload_key])];
                incomeEntry.upload_key = mergedKeys;
                incomeEntry.income_document = allWageInquiryKeys;
                console.log(`[transformUtilityAssistance] Updated unemployed income entry with wage inquiry keys:`, allWageInquiryKeys);
              }
            }
            
            incomeDetails.push(incomeEntry);
            console.log(`[transformUtilityAssistance] Added family member income entry for ${email}`);
          }
        });
      } else if (isUnemployed) {
        // For unemployed family members without income table data, create entry with wage income inquiry upload
        // Get wage income inquiry upload keys from field 1113
        const familyWageInquiryKey = `income_details_1006_1113_${uniqueId}`;
        const familyWageInquiry = formValues[familyWageInquiryKey];
        
        let wageInquiryUploadKeys: string[] = [];
        if (familyWageInquiry) {
          if (Array.isArray(familyWageInquiry)) {
            wageInquiryUploadKeys = familyWageInquiry.filter((v: any) => v && typeof v === 'string');
          } else if (typeof familyWageInquiry === 'string') {
            wageInquiryUploadKeys = familyWageInquiry.split(',').filter((k: string) => k.trim());
          }
        }
        
        // Also check uploadedFilesMap for wage income inquiry files
        const wageInquiryMapKey = `income_details_1006_1113_${uniqueId}`;
        const uploadedWageFiles = uploadedFilesMap[wageInquiryMapKey] || [];
        const mapUploadKeys = uploadedWageFiles
          .filter(uf => uf.key && !uf.uploading && !uf.error)
          .map(uf => uf.key!);
        
        // Merge both sources, remove duplicates
        const allWageInquiryKeys = [...new Set([...wageInquiryUploadKeys, ...mapUploadKeys])];
        
        console.log(`[transformUtilityAssistance] Creating income entry for unemployed family member ${email} (uniqueId: ${uniqueId}), wage inquiry keys:`, allWageInquiryKeys);
        
        const unemployedIncomeEntry: any = {
          email: email,
          family_email: email,
          is_family: true,
          is_unemployed: true,
          identification_number: member['1079'] || '',
          income_type: '',
          income_source: '',
          frequency: '',
          income_amount1: '',
          income_date1: '',
          income_amount2: '',
          income_date2: '',
          income_amount3: '',
          income_date3: '',
          income_amount4: '',
          income_date4: '',
          income_amount5: '',
          income_date5: '',
          income_document: [],
          upload_key: allWageInquiryKeys
        };
        incomeDetails.push(unemployedIncomeEntry);
        console.log(`[transformUtilityAssistance] Added unemployed income entry for family member ${email} with ${allWageInquiryKeys.length} upload keys`);
      } else {
        console.log(`[transformUtilityAssistance] No income data found for family member ${email} (uniqueId: ${uniqueId}), not unemployed`);
      }
    });

    // Filter out income entries for deleted family members
    // Only keep income for family members that still exist in table_1004
    const validFamilyEmails = new Set<string>();
    const validFamilyIdentificationDigits = new Set<string>();
    familyTableDataForEmailMap.forEach((member: any) => {
      const email = member['1097'] || '';
      if (email && email.trim() !== '') {
        validFamilyEmails.add(email.trim().toLowerCase());
        validFamilyEmails.add(email.trim()); // Also add original case
      }
      const idDigits = normalizeIdentificationDigits(member['1079']);
      if (idDigits) {
        validFamilyIdentificationDigits.add(idDigits);
      }
    });
    
    // Filter income details to only include entries for existing family members
    const filteredIncomeDetails = incomeDetails.filter((entry) => {
      // Always keep householder income (is_family is false or undefined)
      if (!entry.is_family || entry.is_family === false) {
        return true;
      }
      
      const entryIdDigits = normalizeIdentificationDigits(entry.identification_number);
      if (entryIdDigits && validFamilyIdentificationDigits.has(entryIdDigits)) {
        return true;
      }

      // For family member income without identification on the entry, fall back to email (legacy)
      const entryEmail = entry.family_email || entry.email || '';
      if (!entryEmail || entryEmail.trim() === '') {
        console.warn('[transformUtilityAssistance] Filtering out family income with no email and no identification_number:', entry);
        return false;
      }
      
      const emailExists = validFamilyEmails.has(entryEmail.trim().toLowerCase()) || 
                          validFamilyEmails.has(entryEmail.trim());
      
      if (!emailExists) {
        console.log(`[transformUtilityAssistance] Filtering out income entry for deleted family member:`, {
          email: entryEmail,
          incomeType: entry.income_type,
          incomeSource: entry.income_source,
          validEmails: Array.from(validFamilyEmails)
        });
        return false;
      }
      
      return true;
    });
    
    // Replace incomeDetails with filtered list
    incomeDetails.length = 0;
    incomeDetails.push(...filteredIncomeDetails);
    
    // Remove duplicate income entries and filter out completely empty entries
    // This prevents duplicate entries for the same person with the same income data
    const seenEntries = new Set<string>();
    const uniqueIncomeDetails = incomeDetails.filter((entry) => {
      // Skip entries that are completely empty (no meaningful data)
      // Upload key alone is NOT sufficient - must have actual income data
      // EXCEPTION: Family member entries (is_family=true) should be kept even if empty
      // This ensures unemployed family members are represented
      const hasData = (entry.income_type && String(entry.income_type).trim() !== '') || 
                     (entry.income_source && String(entry.income_source).trim() !== '' && entry.frequency && String(entry.frequency).trim() !== '') || 
                     (entry.income_amount1 && String(entry.income_amount1).trim() !== '') ||
                     (entry.income_amount2 && String(entry.income_amount2).trim() !== '') ||
                     (entry.income_amount3 && String(entry.income_amount3).trim() !== '') ||
                     (entry.income_amount4 && String(entry.income_amount4).trim() !== '') ||
                     (entry.income_amount5 && String(entry.income_amount5).trim() !== '');
      
      // Keep entries even if empty if they are unemployed (for both family members and householder)
      if (!hasData && (entry.is_unemployed === true || entry.is_unemployed === 'true')) {
        // This is an empty entry for an unemployed member (family or householder), keep it
        const entryEmail = entry.is_family ? (entry.family_email || entry.email || '') : (entry.email || '');
        const idDigits = entry.is_family ? normalizeIdentificationDigits(entry.identification_number) : '';
        if (entry.is_family && !idDigits && (!entryEmail || entryEmail.trim() === '')) {
          return false;
        }
        if (!entry.is_family && (!entryEmail || entryEmail.trim() === '')) {
          return false;
        }
        const personKey = entry.is_family && idDigits ? `id:${idDigits}` : entryEmail;
        const entryKey = `${personKey}_unemployed`;
        if (seenEntries.has(entryKey)) {
          return false; // Skip duplicate unemployed entries for same person
        }
        seenEntries.add(entryKey);
        return true;
      }
      
      // Keep family member entries even if empty (for unemployed members) - legacy support
      if (!hasData && entry.is_family) {
        // This is an empty entry for a family member (likely unemployed), keep it
        const entryEmail = entry.family_email || entry.email || '';
        const idDigits = normalizeIdentificationDigits(entry.identification_number);
        if (!idDigits && (!entryEmail || entryEmail.trim() === '')) {
          return false;
        }
        const personKey = idDigits ? `id:${idDigits}` : entryEmail;
        const entryKey = `${personKey}_empty`;
        if (seenEntries.has(entryKey)) {
          return false; // Skip duplicate empty entries for same family member
        }
        seenEntries.add(entryKey);
        return true;
      }
      
      if (!hasData) {
        return false; // Skip empty entries that are not unemployed
      }
      
      const entryEmail = entry.is_family ? (entry.family_email || entry.email || '') : (entry.email || '');
      const idDigits = entry.is_family ? normalizeIdentificationDigits(entry.identification_number) : '';
      if (entry.is_family && !idDigits && (!entryEmail || entryEmail.trim() === '')) {
        return false;
      }
      if (!entry.is_family && (!entryEmail || entryEmail.trim() === '')) {
        return false;
      }
      
      const personKey =
        entry.is_family && idDigits
          ? `id:${idDigits}`
          : entry.is_family && entry.family_email
            ? entry.family_email
            : entryEmail;
      const entryKey = `${personKey}_${entry.income_type || ''}_${entry.income_source || ''}_${entry.frequency || ''}`;
      
      // Skip if we've seen this exact entry before
      if (seenEntries.has(entryKey)) {
        return false;
      }
      
      // Add to seen set and include in result
      seenEntries.add(entryKey);
      return true;
    });
    
    // Replace incomeDetails with deduplicated list
    incomeDetails.length = 0;
    incomeDetails.push(...uniqueIncomeDetails);
  }

  // 5. Service details with upload keys (skip for Senior Center program)
  const serviceDetails: any[] = [];
  if (!isSeniorCenterProgram) {
  const serviceTableData = formValues['table_1012'] || [];
  
  // Helper function to get bill_type label from ID
  // Helper function to find a service in a list by ID
  const findServiceInList = (serviceList: Array<{id: string | number, label: string, value: any}>, billTypeIdStr: string): {id: string | number, label: string, value: any} | null => {
    if (!Array.isArray(serviceList) || serviceList.length === 0) {
      return null;
    }
    
    return serviceList.find((s) => {
      const serviceId = String(s.id || s.value || '');
      const serviceValue = String(s.value || s.id || '');
      return serviceId === billTypeIdStr || serviceValue === billTypeIdStr;
    }) ?? null;
  };

  // Helper function to extract label from a service object
  const extractServiceLabel = (service: any, billTypeIdStr: string, source: string): string => {
    const label = service.label || String(service.value || service.id || billTypeIdStr);
    console.log(`[transformUtilityAssistance] Found matching service in ${source}, label:`, label);
    return label;
  };

  // Helper function to find field in a group
  const findFieldInGroup = (group: { fields?: Field[] }, fieldId: string): Field | null => {
    if (!group.fields) {
      return null;
    }
    
    return group.fields.find((field) => field.field_id === fieldId) ?? null;
  };

  // Helper function to find field 1077 in config
  const findField1077InConfig = (config: FormBuilderConfig | null): Field | null => {
    if (!config?.sections) {
      return null;
    }
    
    for (const section of config.sections) {
      if (!section.groups) continue;
      
      for (const group of section.groups) {
        const field = findFieldInGroup(group, '1077');
        if (field) {
          return field;
        }
      }
    }
    
    return null;
  };

  // Helper function to find option in field options
  const findOptionInField = (field: { options?: FieldOption[] }, billTypeIdStr: string): FieldOption | null => {
    if (!field.options || !Array.isArray(field.options)) {
      return null;
    }
    
    return field.options.find((opt) => {
      const optValue = String(opt.value || (opt as any).id || '');
      const optId = String((opt as any).id || opt.value || '');
      return optValue === billTypeIdStr || optId === billTypeIdStr;
    }) ?? null;
  };

  // Helper function to get label from config field options
  const getLabelFromConfig = (billTypeIdStr: string): string | null => {
    const field1077 = findField1077InConfig(config);
    if (!field1077) {
      return null;
    }
    
    const option = findOptionInField(field1077, billTypeIdStr);
    if (option) {
      const label = option.label || option.value || billTypeIdStr;
      console.log('[transformUtilityAssistance] Found matching option in config, label:', label);
      return label;
    }
    
    console.log('[transformUtilityAssistance] No matching option found for ID:', billTypeIdStr);
    return null;
  };

  const getBillTypeLabel = (billTypeId: string | null | undefined): string => {
    if (!billTypeId) return '';
    
    const billTypeIdStr = String(billTypeId);
    console.log('[transformUtilityAssistance] Looking up bill_type label for ID:', billTypeIdStr);
    
    // First, try to find in serviceList parameter (passed from effects)
    if (serviceList) {
      const serviceFromParam = findServiceInList(serviceList, billTypeIdStr);
      if (serviceFromParam) {
        return extractServiceLabel(serviceFromParam, billTypeIdStr, 'serviceList parameter');
      }
    }
    
    // Second, try to find in formValues._serviceList (stored when service list is loaded)
    const formServiceList = formValues['_serviceList'];
    const serviceFromForm = findServiceInList(formServiceList, billTypeIdStr);
    if (serviceFromForm) {
      return extractServiceLabel(serviceFromForm, billTypeIdStr, 'formValues._serviceList');
    }
    
    // Fallback: Find field 1077 in config to get its options
    const labelFromConfig = getLabelFromConfig(billTypeIdStr);
    if (labelFromConfig) {
      return labelFromConfig;
    }
    
    // If no option found, return the ID as-is (fallback)
    console.log('[transformUtilityAssistance] Using bill_type ID as fallback:', billTypeIdStr);
    return billTypeIdStr;
  };
  
  serviceTableData.forEach((service: any, index: number) => {
    // Get upload keys from field 1076 (file upload in service table)
    let serviceUploadKeys: string[] = [];
    
    // Try to get from form value first
    if (service['1076']) {
      if (Array.isArray(service['1076'])) {
        serviceUploadKeys = service['1076'].filter((v: any) => v && typeof v === 'string');
      } else if (typeof service['1076'] === 'string') {
        serviceUploadKeys = service['1076'].split(',').filter((k: string) => k.trim());
      }
    }
    
    // Also check uploadedFilesMap with row index
    const serviceSectionId = 'service_details';
    const serviceGroupId = '1012';
    const serviceRowKey = `${serviceSectionId}_${serviceGroupId}_1076_${index}`;
    const uploadedFiles = uploadedFilesMap[serviceRowKey] || [];
    const mapUploadKeys = uploadedFiles
      .filter(uf => uf.key && !uf.uploading && !uf.error)
      .map(uf => uf.key!);
    
    // Merge both sources, remove duplicates
    const uniqueServiceUploadKeys = [...new Set([...serviceUploadKeys, ...mapUploadKeys])];
    
    // Get bill_type ID and convert to label
    const billTypeId = service['1077'] || '';
    const billTypeLabel = getBillTypeLabel(billTypeId);
    
    // Convert field 1073 ("yes"/"no") to boolean for ia_bill_your_name
    const billsInYourNameValue = service['1073'] || '';
    const iaBillYourName = billsInYourNameValue === 'yes' || billsInYourNameValue === 'Yes' || billsInYourNameValue === true;
    
    // Determine account_holder, reason, and bill_name based on whether bills are in user's name
    let accountHolder: string | undefined = undefined;
    let reason: string | undefined = undefined;
    let billName: string = '';
    
    if (iaBillYourName) {
      // When bills are in user's name: use field 1000 (Account Holder dropdown)
      // Only use service-specific value, don't fallback to main form's field 1000
      accountHolder = service['1000'] || undefined;
      billName = accountHolder || '';
    } else {
      // When bills are NOT in user's name: use field 1081 (Account Holder Name) and field 1082 (Reason)
      accountHolder = service['1081'] || undefined;
      reason = service['1082'] || undefined;
      billName = accountHolder || '';
    }
    
    serviceDetails.push({
      bill_name: billName,
      utility_company: service['1074'] || '',
      account_number: service['1075'] || '',
      bill_type: billTypeLabel, // Use label instead of ID
      ia_bill_your_name: iaBillYourName,
      account_holder: accountHolder,
      reason: reason,
      utility_bill_document: service['1076'] || '',
      upload_key: uniqueServiceUploadKeys // No duplicates
    });
  });
  }

  // 6. Application details
  const applicationDetails = {
    household_type: formValues['1022'] || '',
    own_or_rent_address: formValues['1023'] || '',
    pays_rent: formValues['1001'] || '',
    how_much: formValues['1118'] || '',
    payment_frequency: formValues['1114'] || '',
    apartment_or_landlord_name: formValues['1115'] || '',
    landlord_telephone: formValues['1116'] || '',
    landlord_alternate_telephone: formValues['1117'] || '',
    landlord_email: formValues['1119'] || '',
    service_utility: formValues['1024'] || '',
    cool_home: formValues['1025'] || '',
    heat_home: formValues['1026'] || '',
    // Additional props if needed
  };

  // Helper function to check if checkbox value is accepted
  const isCheckboxAccepted = (value: any): boolean => {
    if (Array.isArray(value)) {
      return value.includes('true') || value.includes(true) || value.some(v => v === true || String(v).toLowerCase() === 'true');
    }
    return value === true || value === 'true';
  };

  // Normalize Yes/No values to "yes"/"no"
  const normalizeYesNoValue = (value: any): string => {
    const normalized = String(value).toLowerCase();
    if (normalized === 'yes' || normalized === 'true') return 'yes';
    if (normalized === 'no' || normalized === 'false') return 'no';
    return String(value);
  };

  // 7. Declaration
  const isDeclarationAccepted = Array.isArray(formValues['1057']) 
    ? formValues['1057'].length > 0 
    : formValues['1057'] === true || formValues['1057'] === 'true';

  // 7a. Release of Information (field 1099)
  const isReleaseOfInformationAccepted = isCheckboxAccepted(formValues['1099']);

  // 7b. Applicant Acknowledgement (field 1110)
  const isApplicantAcknowledgementAccepted = isCheckboxAccepted(formValues['1110']);

  // 7c. Weatherization Waiver (required when applying for Wx or Utility interest Yes)
  const isWeatherizationWaiverAccepted = isCheckboxAccepted(formValues['wx_waiver_ack']);

  // Helper function to build weatherization details
  const buildWeatherizationDetails = (formValues: Record<string, any>, programId: number, isSeniorCenterProgram: boolean): Record<string, any> | undefined => {
    if (isSeniorCenterProgram) {
      return undefined;
    }
    
  const weatherizationToggle = formValues['1084'];
  const isWeatherizationProgram = programId === 1001;
  const hasWeatherizationData = isWeatherizationProgram || weatherizationToggle === 'yes';
  
    if (!hasWeatherizationData) {
      return undefined;
    }
    
    const fieldIdToNameMap: Record<string, string> = {
      '1027': 'is_home_drafty',
      '1028': 'received_new_attic_insulation',
      '1029': 'installed_led_bulbs',
      '1030': 'walls_have_insulation',
      '1031': 'refrigerator_age',
      '1032': 'currently_remodeling_home',
      '1033': 'accept_gas_stove_replacement',
      '1034': 'ac_age',
      '1035': 'is_ac_working',
      '1036': 'heater_age',
      '1037': 'is_heat_working',
      '1038': 'is_electric_or_gas',
      '1039': 'has_central_heat_and_air',
      '1040': 'accept_replace_unvented_gas_heaters',
      '1041': 'has_solar_screens_on_windows',
      '1042': 'has_structural_problem',
      '1043': 'has_large_exposure_to_outdoors',
      '1044': 'has_roof_leaks',
      '1045': 'has_moisture_or_mold_problem',
      '1120': 'where_moisture_or_mold_problems',
      '1046': 'has_bugs_rodents_beehives',
      '1047': 'has_unusual_odors_smells',
      '1048': 'has_headache_itchy_eyes_sinus_dizziness',
      '1049': 'risk_family_health_safety',
      '1050': 'hear_about_weatherization_program'
    };
    
    const weatherizationDetails: Record<string, any> = {
      is_applying: true
    };
    
    Object.keys(fieldIdToNameMap).forEach(fieldId => {
      const fieldName = fieldIdToNameMap[fieldId];
      const fieldValue = formValues[fieldId];
      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
        weatherizationDetails[fieldName] = fieldValue;
      }
    });
    
    return weatherizationDetails;
  };

  // 8. Weatherization details (skip for Senior Center program)
  const weatherizationDetails = buildWeatherizationDetails(formValues, programId, isSeniorCenterProgram);

  // 8a. Extract is_apply_weatherization and is_referred as top-level fields
  const getIsApplyWeatherization = (): boolean | null => {
    const field1084Value = formValues['1084'];
    if (field1084Value !== undefined && field1084Value !== null && field1084Value !== '') {
      return field1084Value === 'yes' || field1084Value === 'Yes' ? true : 
             field1084Value === 'no' || field1084Value === 'No' ? false : null;
    }
    return null;
  };

  const getIsReferred = (): boolean | null => {
    const field1111Value = formValues['1111'];
    if (field1111Value !== undefined && field1111Value !== null && field1111Value !== '') {
      return field1111Value === 'yes' || field1111Value === 'Yes' ? true : 
             field1111Value === 'no' || field1111Value === 'No' ? false : null;
    }
    return null;
  };

  const isApplyWeatherization = getIsApplyWeatherization();
  const isReferred = getIsReferred();

  // Helper function to add primary contact fields for Senior Center program
  const addPrimaryContactFields = (payload: any, formValues: Record<string, any>): void => {
    payload.first_name = formValues['1012'] || '';
    payload.last_name = formValues['1013'] || '';
    payload.dob = formatDate(formValues['1014']);
    payload.gender = formValues['1015'] || '';
    payload.race = formValues['1016'] || '';
    payload.ethnicity = formValues['1017'] || '';
    payload.military_status = formValues['1018'] || '';
    payload.is_migrant_farm_worker = formValues['1019'] || '';
    payload.is_disabled = formValues['1004'] || '';
    payload.citizenship = formValues['1020'] || '';
    payload.is_receiving_income = toYesNo(formValues['1021']) || '';
    if (formValues['1067']) {
      payload.primary_contact_email = formValues['1067'];
    }
  };

  // Helper function to add standard program fields
  const addStandardProgramFields = (
    payload: any,
    options: {
      verifiedIdentityKeys: string[];
      verifiedCitizenshipKeys: string[];
      proofOfIdentity: string[];
      proofOfCitizenship: string[];
      familyMembers: any[];
      incomeDetails: any[];
      serviceDetails: any[];
      formValues: Record<string, any>;
      isDeclarationAccepted: boolean;
      isApplicantAcknowledgementAccepted: boolean;
      weatherizationDetails?: Record<string, any>;
    }
  ): void => {
    payload.identity_upload_key = options.verifiedIdentityKeys;
    payload.citizenship_upload_key = options.verifiedCitizenshipKeys;
    payload.proof_of_identity = options.proofOfIdentity.length > 0 ? options.proofOfIdentity[0] : undefined;
    payload.proof_of_citizenship = options.proofOfCitizenship.length > 0 ? options.proofOfCitizenship[0] : undefined;
    payload.family_members = options.familyMembers;
    payload.income_details = options.incomeDetails;
    payload.service_details = options.serviceDetails;
    
    // Handle field 1058 (applicant name) - concatenate FirstName, LastName, and Initial
    // Check if we have split parts stored separately
    const firstName = options.formValues['1058_first'] || '';
    const lastName = options.formValues['1058_last'] || '';
    const initial = options.formValues['1058_initial'] || '';
    
    if (firstName || lastName || initial) {
      // Concatenate the parts
      const parts = [firstName, lastName, initial].filter(part => part && part.trim().length > 0);
      payload.applicant_sign = parts.join(' ').trim();
    } else {
      // Fallback to the main field value if split parts are not available
      payload.applicant_sign = options.formValues['1058'] || '';
    }
    
    payload.is_declaration_accepted = options.isDeclarationAccepted;
    payload.applicant_acknowledgement = options.isApplicantAcknowledgementAccepted;
    const acknowledgementDate = options.formValues['acknowledgement_date'];
    payload.acknowledgement_date = acknowledgementDate instanceof Date
      ? formatDate(acknowledgementDate)
      : acknowledgementDate && typeof acknowledgementDate === 'string'
        ? acknowledgementDate
        : null;

    if (options.weatherizationDetails && Object.keys(options.weatherizationDetails).length > 0) {
      payload.weatherization_details = options.weatherizationDetails;
    }
  };

  // Final payload - ensure root level upload keys are split between identity and citizenship
  const finalPayload: any = {
    program_id: programId,
    household_name: formValues['1000'] || '',
    email_address: householderEmail, // Use the email we got earlier
    phone: formValues['1006'] ? formValues['1006'].replace(/\D/g, '').slice(0, 10) : '',
    address: addresses,
    application_details: applicationDetails,
    release_of_information: isReleaseOfInformationAccepted,
    status: status
  };

  if (!isSeniorCenterProgram) {
    finalPayload.is_migrant_farm_worker = toYesNo(formValues['1102'] ?? formValues['1019']) || '';
    finalPayload.is_disabled = toYesNo(formValues['1105'] ?? formValues['1004']) || '';
    // Prefer Family Members table (1004) primary row 1104 — top-level 1104/1021 are often empty.
    const familyTable = Array.isArray(formValues['table_1004']) ? formValues['table_1004'] : [];
    const householderEmailNorm = String(
      formValues['1005'] || formValues['email_address'] || ''
    )
      .trim()
      .toLowerCase();
    const householderFromTable =
      familyTable.find(
        (m: any) =>
          String(m?.['1097'] || '')
            .trim()
            .toLowerCase() === householderEmailNorm && !!householderEmailNorm
      ) || (familyTable.length > 0 ? familyTable[0] : null);
    finalPayload.is_receiving_income =
      toYesNo(
        householderFromTable?.['1104'] ?? formValues['1104'] ?? formValues['1021']
      ) || '';
  }

  // Persist Wx waiver when applicable (standalone Wx or Utility + interest)
  const shouldIncludeWeatherizationWaiver =
    programId === 1001 || isApplyWeatherization === true;
  if (shouldIncludeWeatherizationWaiver) {
    finalPayload.weatherization_waiver = isWeatherizationWaiverAccepted;
  }

  // Add is_apply_weatherization and is_referred as top-level fields
  if (isApplyWeatherization !== null) {
    finalPayload.is_apply_weatherization = isApplyWeatherization;
  }
  if (isReferred !== null) {
    finalPayload.is_referred = isReferred;
  }
  
  if (isSeniorCenterProgram) {
    addPrimaryContactFields(finalPayload, formValues);
  } else {
    addStandardProgramFields(finalPayload, {
      verifiedIdentityKeys,
      verifiedCitizenshipKeys,
      proofOfIdentity: rootProofOfIdentity,
      proofOfCitizenship: rootProofOfCitizenship,
      familyMembers,
      incomeDetails,
      serviceDetails,
      formValues,
      isDeclarationAccepted,
      isApplicantAcknowledgementAccepted,
      weatherizationDetails
    });
  }
  
  return finalPayload;
}

/**
 * Merge table_data from static JSON config into formValues when store is missing rows
 * (e.g. table keys are not on the reactive FormGroup and never synced to NgRx).
 */
export function enrichSeniorCenterFormValuesFromConfig(
  formValues: Record<string, any>,
  config: FormBuilderConfig | null
): Record<string, any> {
  const merged: Record<string, any> = { ...formValues };
  if (!config?.sections) {
    return merged;
  }
  for (const section of config.sections) {
    for (const group of section.groups || []) {
      if (!group?.is_table || !group.group_id) {
        continue;
      }
      const key = `table_${group.group_id}`;
      const td = group.table_data;
      if (!Array.isArray(td) || td.length === 0) {
        continue;
      }
      if (!Array.isArray(merged[key]) || merged[key].length === 0) {
        merged[key] = JSON.parse(JSON.stringify(td));
      }
    }
  }
  return merged;
}

function omitEmptyStringLeaves(obj: unknown): void {
  if (!obj || typeof obj !== 'object') {
    return;
  }
  if (Array.isArray(obj)) {
    obj.forEach(item => {
      if (item && typeof item === 'object') {
        omitEmptyStringLeaves(item);
      }
    });
    return;
  }
  const record = obj as Record<string, unknown>;
  for (const k of Object.keys(record)) {
    const v = record[k];
    if (v === '') {
      delete record[k];
    } else if (v && typeof v === 'object') {
      omitEmptyStringLeaves(v);
    }
  }
}

/**
 * Transform form values to Senior Center Program API payload
 * @param formValues - All form field values
 * @param config - Form configuration
 * @param uploadedFilesMap - Map of uploaded files (key: uniqueKey, value: UploadedFile[])
 * @param programId - Program ID (should be 1000 for Senior Center Program)
 * @param status - Submission status ('draft', 'pending', or 're-submit')
 */
export function transformSeniorCenterProgram(
  formValues: Record<string, any>,
  config: FormBuilderConfig | null,
  uploadedFilesMap: Record<string, UploadedFile[]>,
  programId: number = 1000,
  status: 'draft' | 'pending' | 're-submit' = 'pending'
): SeniorCenterProgramPayload {
  
  // Helper to format date to YYYY-MM-DD (local calendar; no UTC shift)
  const formatDate = (date: any): string => DateUtils.toApiDateString(date);

  // Helper to calculate age from DOB
  const calculateAge = (dob: any): string => {
    if (!dob) return '';
    try {
      const birthDate = DateUtils.coerceToLocalDate(dob);
      if (!birthDate) return '';
      
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return String(age >= 0 ? age : '');
    } catch {
      return '';
    }
  };

  const isOtherSelected = (value: any): boolean => {
    if (Array.isArray(value)) {
      return value.some(item => String(item).toLowerCase() === 'other');
    }
    return String(value || '').toLowerCase() === 'other';
  };

  const getSpecifyOtherValue = (value: any, specifyOther: any): string => {
    if (!isOtherSelected(value)) return '';
    return specifyOther !== undefined && specifyOther !== null ? String(specifyOther) : '';
  };

  const uniqueStemFromRecipientId = (id: string): string => {
    const parts = String(id || '')
      .split('_')
      .filter(Boolean);
    if (parts.length < 2) {
      return String(id || '').toLowerCase();
    }
    return `${parts[0]}_${parts[1]}`.toLowerCase();
  };

  const resolveNutritionControl = (baseId: string, uniqueId: string): any => {
    const exactKey = `${baseId}_${uniqueId}`;
    if (Object.prototype.hasOwnProperty.call(formValues, exactKey)) {
      return formValues[exactKey];
    }
    const prefix = `${baseId}_`;
    const stem = uniqueStemFromRecipientId(uniqueId);
    const looseKeys: string[] = [];
    for (const k of Object.keys(formValues)) {
      if (!k.startsWith(prefix)) {
        continue;
      }
      const suffix = k.slice(prefix.length);
      if (suffix === uniqueId) {
        return formValues[k];
      }
      if (stem && uniqueStemFromRecipientId(suffix) === stem) {
        looseKeys.push(k);
      }
    }
    if (looseKeys.length === 1) {
      return formValues[looseKeys[0]];
    }
    return undefined;
  };

  const isNutritionChecked = (value: any): boolean => {
    if (value === undefined || value === null) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.some(
        v =>
          v === true ||
          v === 'true' ||
          v === 'yes' ||
          String(v).toLowerCase() === 'yes' ||
          String(v).toLowerCase() === 'true'
      );
    }
    return (
      value === true ||
      value === 'true' ||
      value === 'yes' ||
      String(value).toLowerCase() === 'yes' ||
      String(value).toLowerCase() === 'true'
    );
  };

  // Helper to calculate nutrition score and risk level from recipient nutrition fields
  const calculateNutritionAssessment = (
    uniqueId: string
  ): { nutrition_risk_questions: string[]; total_score: number; risk_level: string } | undefined => {
    const nutritionQuestions: string[] = [];
    const questionFields = [
      'nutrition_risk_q1',
      'nutrition_risk_q2',
      'nutrition_risk_q3',
      'nutrition_risk_q4',
      'nutrition_risk_q5',
      'nutrition_risk_q6',
      'nutrition_risk_q7',
      'nutrition_risk_q8',
      'nutrition_risk_q9',
      'nutrition_risk_q10'
    ];

    const fieldIdToApiLabel: Record<string, string> = {
      nutrition_risk_q1: 'I have an illness or condition that has changed the kind or amount of food I eat',
      nutrition_risk_q2: 'Eats fewer than 2 meals per day',
      nutrition_risk_q3: 'I eat few fruits, vegetables, or milk products',
      nutrition_risk_q4: 'I have three or more alcoholic drinks almost every day',
      nutrition_risk_q5: 'I have tooth or mouth problems that make it hard to eat',
      nutrition_risk_q6: "I don't always have enough money to buy the food I need",
      nutrition_risk_q7: 'I eat alone',
      nutrition_risk_q8: 'I take three or more different prescribed or over-the-counter drugs a day',
      nutrition_risk_q9: 'Weight loss in past 6 months',
      nutrition_risk_q10: 'I am not always physically able to shop, cook, and/or feed myself'
    };

    const noneValue = resolveNutritionControl('nutrition_risk_none', uniqueId);
    const noneSelected = isNutritionChecked(noneValue);

    if (noneSelected) {
      return {
        nutrition_risk_questions: [],
        total_score: 0,
        risk_level: 'Good'
      };
    }

    questionFields.forEach(fieldId => {
      const value = resolveNutritionControl(fieldId, uniqueId);
      if (isNutritionChecked(value)) {
        const apiLabel = fieldIdToApiLabel[fieldId];
        if (apiLabel) {
          nutritionQuestions.push(apiLabel);
        }
      }
    });

    const totalScore = nutritionQuestions.length;
    let riskLevel = 'Good';
    if (totalScore >= 6) {
      riskLevel = 'High Nutritional Risk';
    } else if (totalScore >= 3) {
      riskLevel = 'Moderate Nutritional Risk';
    }

    const totalScoreControl = resolveNutritionControl('nutrition_total_score', uniqueId);
    const riskLevelControl = resolveNutritionControl('nutrition_risk_level', uniqueId);

    if (
      nutritionQuestions.length > 0 ||
      totalScoreControl !== undefined ||
      riskLevelControl !== undefined
    ) {
      return {
        nutrition_risk_questions: nutritionQuestions,
        total_score: totalScore,
        risk_level: riskLevel
      };
    }

    return undefined;
  };

  // Helper function to build addresses array
  const buildAddresses = (formValues: Record<string, any>): any[] => {
    const addresses: any[] = [];
    
    if (formValues['1007'] || formValues['1008'] || formValues['1009']) {
      addresses.push({
        address_type: formValues['1060'] || 'residential',
        address_line: formValues['1007'] || '',
        apartment_number: formValues['1092'] || '',
        postal_code: formValues['1008'] || '',
        city: formValues['1009'] || '',
        state: formValues['1010'] || '',
        country: formValues['1011'] || '',
        county: formValues['1091'] || ''
      });
    }

    const hasMailingAddress = isCheckboxAccepted(formValues['1059']);
    
    if (hasMailingAddress && (formValues['1061'] || formValues['1062'] || formValues['1063'])) {
      addresses.push({
        address_type: formValues['1066'] || 'mailing',
        address_line: formValues['1061'] || '',
        apartment_number: formValues['1109'] || '',
        postal_code: formValues['1062'] || '',
        city: formValues['1063'] || '',
        state: formValues['1064'] || '',
        country: formValues['1065'] || '',
        county: formValues['1083'] || ''
      });
    }

    return addresses;
  };

  // Helper function to check if checkbox value is accepted
  const isCheckboxAccepted = (value: any): boolean => {
    if (Array.isArray(value)) {
      return value.includes('true') || value.includes(true) || value.some(v => v === true || String(v).toLowerCase() === 'true');
    }
    return value === true || value === 'true';
  };

  const isCheckboxOrYesAccepted = (value: any): boolean => {
    if (isCheckboxAccepted(value)) {
      return true;
    }
    if (Array.isArray(value)) {
      return value.some(v => v === 'yes' || String(v).toLowerCase() === 'yes');
    }
    return value === 'yes' || String(value).toLowerCase() === 'yes';
  };

  // Build addresses
  const addresses = buildAddresses(formValues);

  // Normalize Yes/No values to "yes"/"no"
  const normalizeYesNoValue = (value: any): string => {
    const normalized = String(value).toLowerCase();
    if (normalized === 'yes' || normalized === 'true') return 'yes';
    if (normalized === 'no' || normalized === 'false') return 'no';
    return String(value);
  };

  const pickStr = (...keys: string[]): string => {
    for (const key of keys) {
      if (!key) continue;
      const raw = formValues[key];
      if (raw !== undefined && raw !== null && raw !== '') {
        return typeof raw === 'string' ? raw : String(raw);
      }
    }
    return '';
  };

  const pickRaw = (...keys: string[]): any => {
    for (const key of keys) {
      if (!key) continue;
      const raw = formValues[key];
      if (raw !== undefined && raw !== null && raw !== '') {
        return raw;
      }
    }
    return undefined;
  };

  const collectRecipientsTableData = (): any[] => {
    // Senior Center recipients are stored in table_recipient_information.
    // table_1004 is a different table used by other program flows, so keep it as a last fallback.
    const direct =
      formValues['table_recipient_information'] ||
      formValues['recipients'] ||
      formValues['table_1004'];
    if (Array.isArray(direct) && direct.length > 0) {
      return direct;
    }
    if (config?.sections) {
      for (const section of config.sections) {
        const g = section.groups?.find(
          (gr: any) => gr.group_id === 'recipient_information' && gr.is_table
        );
        if (Array.isArray(g?.table_data) && g.table_data.length > 0) {
          return JSON.parse(JSON.stringify(g.table_data));
        }
      }
    }
    return [];
  };

  const dobForUniqueId = (dobRaw: any): string => DateUtils.toApiDateString(dobRaw);

  const buildRecipientUniqueId = (recipient: any, index: number): string => {
    const fn = String(
      recipient['1094'] || recipient['first_name'] || recipient['recipient_first_name'] || ''
    ).trim();
    const ln = String(
      recipient['1095'] || recipient['last_name'] || recipient['recipient_last_name'] || ''
    ).trim();
    const dobStr = dobForUniqueId(
      recipient['1096'] || recipient['dob'] || recipient['recipient_date_of_birth']
    );
    let id = `${fn}_${ln}_${dobStr}`.replace(/[^a-zA-Z0-9_]/g, '_');
    if (!id || id === '_' || id === '__') {
      id = `recipient_${index}`;
    }
    return id;
  };

  const findHealthPlanRowsForRecipient = (uniqueId: string, fn: string, ln: string): any[] => {
    const prefix = 'table_recipient_health_plan_accordion_';
    const primary = formValues[`${prefix}${uniqueId}`];
    if (Array.isArray(primary) && primary.length > 0) {
      return primary;
    }
    const stem = `${String(fn).trim()}_${String(ln).trim()}`
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .toLowerCase();
    const loose: { key: string; dist: number }[] = [];
    for (const k of Object.keys(formValues)) {
      if (!k.startsWith(prefix)) {
        continue;
      }
      const suf = k.slice(prefix.length).toLowerCase();
      const arr = formValues[k];
      if (!Array.isArray(arr) || arr.length === 0) {
        continue;
      }
      if (suf === String(uniqueId).toLowerCase()) {
        return arr;
      }
      if (stem && suf.startsWith(`${stem}_`)) {
        loose.push({ key: k, dist: Math.abs(suf.length - String(uniqueId).length) });
      }
    }
    if (loose.length === 1) {
      return formValues[loose[0].key];
    }
    if (loose.length > 1) {
      loose.sort((a, b) => a.dist - b.dist);
      return formValues[loose[0].key];
    }
    return [];
  };

  // Build recipients array from table_1004 or recipients table
  const recipients: any[] = [];
  const recipientsTableData = collectRecipientsTableData();

  recipientsTableData.forEach((recipient: any, index: number) => {
    const recipientFirstName = recipient['1094'] || recipient['first_name'] || recipient['recipient_first_name'] || '';
    const recipientLastName = recipient['1095'] || recipient['last_name'] || recipient['recipient_last_name'] || '';
    const recipientFullName = [recipientFirstName, recipientLastName].filter(part => String(part).trim()).join(' ').trim();
    const recipientEmail =
      recipient['recipient_email'] || recipient['email'] || recipient['email_address'] || '';

    const recipientGenderValue =
      recipient['1098'] ||
      recipient['sex'] ||
      recipient['gender'] ||
      recipient['recipient_gender'] ||
      recipient['recipient_sex'] ||
      '';
    const recipientRaceValue = recipient['1099'] || recipient['race'] || recipient['recipient_race'] || '';
    const recipientObj: any = {
      recipient_name: recipientFullName,
      recipient_email: recipientEmail,
      name: recipientFullName,
      email: recipientEmail,
      first_name: recipientFirstName,
      last_name: recipientLastName,
      middle_initial:
        recipient['middle_initial'] ||
        recipient['recipient_middle_initial'] ||
        recipient['middle_name'] ||
        '',
      dob: formatDate(recipient['1096'] || recipient['dob'] || recipient['recipient_date_of_birth']),
      sex: recipientGenderValue,
      race: recipientRaceValue,
      ethnicity: recipient['1100'] || recipient['ethnicity'] || recipient['recipient_ethnicity'] || '',
      marital_status: recipient['marital_status'] || recipient['recipient_marital_status'] || '',
      age: recipient['1080'] || recipient['age'] || calculateAge(recipient['1096'] || recipient['dob'] || recipient['recipient_date_of_birth'])
    };

    const recipientSsn = String(recipient['recipient_ssn'] ?? recipient['ssn'] ?? '').trim();
    if (recipientSsn) {
      recipientObj.ssn = recipientSsn;
    }

    const recipientPhoneFormatted = String(
      recipient['recipient_phone'] ?? recipient['phone'] ?? recipient['phone_number'] ?? '',
    ).trim();
    if (recipientPhoneFormatted) {
      recipientObj.phone = recipientPhoneFormatted;
      recipientObj.recipient_phone = recipientPhoneFormatted;
    }

    if (recipient['recipient_is_second_senior_spouse'] === true || recipient['recipient_is_second_senior_spouse'] === 'true') {
      recipientObj.recipient_is_second_senior_spouse = true;
    }

    const recipientGenderSpecifyOther = getSpecifyOtherValue(
      recipientGenderValue,
      recipient['recipient_gender_specify_other']
    );
    if (recipientGenderSpecifyOther) {
      recipientObj.recipient_gender_specify_other = recipientGenderSpecifyOther;
    }

    const recipientRaceSpecifyOther = getSpecifyOtherValue(
      recipientRaceValue,
      recipient['recipient_race_specify_other']
    );
    if (recipientRaceSpecifyOther) {
      recipientObj.recipient_race_specify_other = recipientRaceSpecifyOther;
    }

    const requestEscortValue = recipient['requires_escort'] ?? recipient['request_escort'];
    if (requestEscortValue !== undefined && requestEscortValue !== null && requestEscortValue !== '') {
      recipientObj.request_escort = normalizeYesNoValue(requestEscortValue);
    }

    const canStepUpValue = recipient['can_step_up_to_van'];
    if (canStepUpValue !== undefined && canStepUpValue !== null && canStepUpValue !== '') {
      recipientObj.can_step_up_to_van = normalizeYesNoValue(canStepUpValue);
    }

    const recipientAreYouDisabled = recipient['recipient_are_you_disabled'];
    if (
      recipientAreYouDisabled !== undefined &&
      recipientAreYouDisabled !== null &&
      String(recipientAreYouDisabled).trim() !== ''
    ) {
      recipientObj.is_disabled = normalizeYesNoValue(recipientAreYouDisabled);
    }

    const recipientAreYouReceivingIncome = recipient['recipient_are_you_receiving_income'];
    if (
      recipientAreYouReceivingIncome !== undefined &&
      recipientAreYouReceivingIncome !== null &&
      String(recipientAreYouReceivingIncome).trim() !== ''
    ) {
      recipientObj.is_receiving_income = normalizeYesNoValue(recipientAreYouReceivingIncome);
    }

    const mobilityDeviceMapping: Record<string, string> = {
      mobility_assistive_device_cane: 'cane',
      mobility_assistive_device_walker: 'walker',
      mobility_assistive_device_wheelchair: 'wheelchair',
      mobility_assistive_device_brace: 'brace',
      mobility_assistive_device_crutches: 'crutches',
      mobility_assistive_device_pov: 'pov',
      mobility_assistive_device_other: 'other',
      mobility_assistive_device_no_device: 'no device'
    };

    const mobilityAssistiveDevices = Object.keys(mobilityDeviceMapping).reduce((devices: string[], fieldId) => {
      if (isCheckboxOrYesAccepted(recipient[fieldId])) {
        devices.push(mobilityDeviceMapping[fieldId]);
      }
      return devices;
    }, []);

    if (mobilityAssistiveDevices.length > 0) {
      recipientObj.mobility_assistive_devices = mobilityAssistiveDevices;
    }

    const mobilityOtherSpecify = getSpecifyOtherValue(
      mobilityAssistiveDevices,
      recipient['mobility_assistive_device_other_specify_other']
    );
    if (mobilityOtherSpecify) {
      recipientObj.mobility_assistive_device_other_specify_other = mobilityOtherSpecify;
    }

    // Add spouse if exists (support nested or flat fields)
    const spouseSource = recipient['spouse'] || {};
    const spouseFirstName = recipient['spouse_first_name'] || spouseSource['first_name'] || '';
    const spouseLastName = recipient['spouse_last_name'] || spouseSource['last_name'] || '';
    const spouseHasData = [
      spouseFirstName,
      spouseLastName,
      recipient['spouse_middle_initial'],
      recipient['spouse_date_of_birth'],
      recipient['spouse_gender'],
      recipient['spouse_race'],
      recipient['spouse_ethnicity'],
      recipient['spouse_marital_status'],
      recipient['spouse_email'],
      recipient['spouse_ssn'],
      spouseSource['middle_initial'],
      spouseSource['dob'],
      spouseSource['sex'],
      spouseSource['gender'],
      spouseSource['race'],
      spouseSource['ethnicity'],
      spouseSource['marital_status'],
      spouseSource['email'],
      spouseSource['ssn']
    ].some(value => value !== undefined && value !== null && String(value).trim() !== '');

    if (spouseHasData) {
      const spouseGenderValue =
        recipient['spouse_gender'] || recipient['spouse_sex'] || spouseSource['sex'] || spouseSource['gender'] || '';
      const spouseRaceValue = recipient['spouse_race'] || spouseSource['race'] || '';
      const spouseGenderSpecifyOther = getSpecifyOtherValue(
        spouseGenderValue,
        recipient['spouse_gender_specify_other'] || spouseSource['spouse_gender_specify_other']
      );
      const spouseRaceSpecifyOther = getSpecifyOtherValue(
        spouseRaceValue,
        recipient['spouse_race_specify_other'] || spouseSource['spouse_race_specify_other']
      );
      const spouseAreYouDisabled = recipient['spouse_are_you_disabled'];
      const spouseAreYouReceivingIncome = recipient['spouse_are_you_receiving_income'];
      const spouseEmail =
        String(recipient['spouse_email'] ?? spouseSource['email'] ?? '').trim() || undefined;
      const spousePhone =
        String(recipient['spouse_phone'] ?? spouseSource['phone'] ?? '').trim() || undefined;
      const spouseSsn = String(recipient['spouse_ssn'] ?? spouseSource['ssn'] ?? '').trim() || undefined;
      const spouseLinkedKey =
        String(recipient['spouse_linked_member_key'] ?? spouseSource['linked_member_key'] ?? '').trim() ||
        undefined;
      const spouseLinkedSource =
        String(recipient['spouse_linked_source'] ?? spouseSource['linked_source'] ?? '').trim() ||
        undefined;
      const spouseFromHousehold =
        String(recipient['spouse_from_household'] ?? spouseSource['from_household'] ?? '').trim() ||
        undefined;
      recipientObj.spouse = {
        first_name: spouseFirstName || '',
        last_name: spouseLastName || '',
        middle_initial: recipient['spouse_middle_initial'] || spouseSource['middle_initial'] || '',
        dob: formatDate(recipient['spouse_date_of_birth'] || spouseSource['dob']),
        sex: spouseGenderValue,
        spouse_gender_specify_other: spouseGenderSpecifyOther || undefined,
        race: spouseRaceValue,
        spouse_race_specify_other: spouseRaceSpecifyOther || undefined,
        ethnicity: recipient['spouse_ethnicity'] || spouseSource['ethnicity'] || '',
        marital_status: recipient['spouse_marital_status'] || spouseSource['marital_status'] || '',
        ...(spouseEmail ? { email: spouseEmail } : {}),
        ...(spousePhone ? { phone: spousePhone } : {}),
        ...(spouseSsn ? { ssn: spouseSsn } : {}),
        ...(spouseLinkedKey ? { linked_member_key: spouseLinkedKey } : {}),
        ...(spouseLinkedSource ? { linked_source: spouseLinkedSource } : {}),
        ...(spouseFromHousehold && spouseFromHousehold !== '__manual__'
          ? { from_household: spouseFromHousehold }
          : {})
      };
      if (
        spouseAreYouDisabled !== undefined &&
        spouseAreYouDisabled !== null &&
        String(spouseAreYouDisabled).trim() !== ''
      ) {
        recipientObj.spouse.is_disabled = normalizeYesNoValue(spouseAreYouDisabled);
      }
      if (
        spouseAreYouReceivingIncome !== undefined &&
        spouseAreYouReceivingIncome !== null &&
        String(spouseAreYouReceivingIncome).trim() !== ''
      ) {
        recipientObj.spouse.is_receiving_income = normalizeYesNoValue(spouseAreYouReceivingIncome);
      }
    }

    const recipientUniqueId = buildRecipientUniqueId(recipient, index);
    const recipientHealthPlansData = findHealthPlanRowsForRecipient(
      recipientUniqueId,
      recipientFirstName,
      recipientLastName
    );
    
    if (Array.isArray(recipientHealthPlansData) && recipientHealthPlansData.length > 0) {
      const healthPlans: any[] = [];
      recipientHealthPlansData.forEach((plan: any) => {
        const insuranceProgramTypeValue = plan['health_plan_insurance_program_type'] || plan['insurance_program_type'] || '';
        const healthPlanNameValue = plan['health_plan_name'] || plan['health_plan'] || '';
        const insuranceProgramTypeSpecifyOther = getSpecifyOtherValue(
          insuranceProgramTypeValue,
          plan['health_plan_insurance_program_type_specify_other']
        );
        const healthPlanNameSpecifyOther = getSpecifyOtherValue(
          healthPlanNameValue,
          plan['health_plan_name_specify_other']
        );
        healthPlans.push({
          insurance_program_type: insuranceProgramTypeValue,
          insurance_program_type_specify_other: insuranceProgramTypeSpecifyOther || undefined,
          id: plan['health_plan_id'] || plan['id'] || '',
          id_response: plan['health_plan_id_response'] || plan['id_response'] || undefined,
          health_plan: healthPlanNameValue,
          health_plan_name_specify_other: healthPlanNameSpecifyOther || undefined
        });
      });
      if (healthPlans.length > 0) {
        recipientObj.health_plans = healthPlans;
      }
    }

    recipients.push(recipientObj);
  });

  // Build emergency contacts array
  const emergencyContacts: any[] = [];
  const emergencyContactsData = Array.isArray(formValues['table_emergency_contacts']) && formValues['table_emergency_contacts'].length > 0
    ? formValues['table_emergency_contacts']
    : (formValues['emergency_contacts'] || []);
  
  emergencyContactsData.forEach((contact: any) => {
    const communicationRestrictions = [
      contact['emergency_contact_restriction_medical'] ? 'Medical Information' : null,
      contact['emergency_contact_restriction_financial'] ? 'Financial Information' : null,
      contact['emergency_contact_restriction_medications'] ? 'Medications' : null,
      contact['emergency_contact_restriction_home_care'] ? 'Home Care Services' : null,
      contact['emergency_contact_restriction_other'] ? 'Other' : null
    ].filter(Boolean);

    const relationshipValue = contact['emergency_contact_relationship'] || contact['relationship'] || '';
    const relationshipSpecifyOther = getSpecifyOtherValue(
      relationshipValue,
      contact['emergency_contact_relationship_specify_other']
    );
    const contactObj: any = {
      first_name: contact['emergency_contact_first_name'] || contact['first_name'] || '',
      last_name: contact['emergency_contact_last_name'] || contact['last_name'] || '',
      phone: contact['emergency_contact_phone'] || contact['phone'] || '',
      email: contact['emergency_contact_email'] || contact['email'] || '',
      contact_preference: contact['emergency_contact_contact_preference'] || contact['contact_preference'] || '',
      relationship: relationshipValue,
      emergency_contact_relationship_specify_other: relationshipSpecifyOther || undefined
    };

    if (communicationRestrictions.length > 0) {
      contactObj.communication_restrictions = communicationRestrictions;
    }

    if (contact['emergency_contact_additional_restriction_info'] || contact['additional_restriction_info']) {
      contactObj.additional_restriction_info =
        contact['emergency_contact_additional_restriction_info'] || contact['additional_restriction_info'];
    }

    // Add addresses if exists
    if (contact['addresses'] && Array.isArray(contact['addresses'])) {
      contactObj.addresses = contact['addresses'];
    } else {
      contactObj.addresses = [
        {
          address_type: '',
          address_line: '',
          apartment_number: '',
          postal_code: '',
          city: '',
          state: '',
          country: '',
          county: ''
        }
      ];
    }

    emergencyContacts.push(contactObj);
  });

  // Build health plans array from per-recipient accordion tables
  const healthPlans: any[] = [];
  let hasHealthPlanTableData = false;
  recipientsTableData.forEach((recipient: any, index: number) => {
    const recipientFirstName = recipient['1094'] || recipient['first_name'] || recipient['recipient_first_name'] || '';
    const recipientLastName = recipient['1095'] || recipient['last_name'] || recipient['recipient_last_name'] || '';
    const recipientFullName = [recipientFirstName, recipientLastName].filter(part => String(part).trim()).join(' ').trim();
    const recipientEmail = recipient['recipient_email'] || recipient['email'] || '';
    const recipientUniqueId = buildRecipientUniqueId(recipient, index);
    const recipientHealthPlansData = findHealthPlanRowsForRecipient(
      recipientUniqueId,
      recipientFirstName,
      recipientLastName
    );
    if (Array.isArray(recipientHealthPlansData) && recipientHealthPlansData.length > 0) {
      hasHealthPlanTableData = true;
      recipientHealthPlansData.forEach((plan: any) => {
        const insuranceProgramTypeValue = plan['health_plan_insurance_program_type'] || plan['insurance_program_type'] || '';
        const healthPlanNameValue = plan['health_plan_name'] || plan['health_plan'] || '';
        const insuranceProgramTypeSpecifyOther = getSpecifyOtherValue(
          insuranceProgramTypeValue,
          plan['health_plan_insurance_program_type_specify_other']
        );
        const healthPlanNameSpecifyOther = getSpecifyOtherValue(
          healthPlanNameValue,
          plan['health_plan_name_specify_other']
        );
        healthPlans.push({
          recipient_name: recipientFullName,
          recipient_email: recipientEmail,
          insurance_program_type: insuranceProgramTypeValue,
          insurance_program_type_specify_other: insuranceProgramTypeSpecifyOther || undefined,
          id: plan['health_plan_id'] || plan['id'] || '',
          id_response: plan['health_plan_id_response'] || plan['id_response'] || undefined,
          health_plan: healthPlanNameValue,
          health_plan_name_specify_other: healthPlanNameSpecifyOther || undefined,
          upload_key: plan['health_plan_attachment'] || plan['upload_key'] || []
        });
      });
    }
  });
  if (!hasHealthPlanTableData && Array.isArray(formValues['health_plans'])) {
    formValues['health_plans'].forEach((plan: any) => {
      const insuranceProgramTypeValue = plan.insurance_program_type || '';
      const healthPlanNameValue = plan.health_plan || '';
      const insuranceProgramTypeSpecifyOther = getSpecifyOtherValue(
        insuranceProgramTypeValue,
        plan.health_plan_insurance_program_type_specify_other
      );
      const healthPlanNameSpecifyOther = getSpecifyOtherValue(
        healthPlanNameValue,
        plan.health_plan_name_specify_other
      );
      healthPlans.push({
        recipient_name: plan.recipient_name || '',
        recipient_email: plan.recipient_email || '',
        insurance_program_type: insuranceProgramTypeValue,
        insurance_program_type_specify_other: insuranceProgramTypeSpecifyOther || undefined,
        id: plan.id || '',
        id_response: plan.id_response || undefined,
        health_plan: healthPlanNameValue,
        health_plan_name_specify_other: healthPlanNameSpecifyOther || undefined,
        upload_key: plan.upload_key || []
      });
    });
  }

  // Build nutritional assessment (per recipient)
  const nutritionalAssessment: any[] = [];
  recipientsTableData.forEach((recipient: any, index: number) => {
    const recipientFirstName = recipient['1094'] || recipient['first_name'] || recipient['recipient_first_name'] || '';
    const recipientLastName = recipient['1095'] || recipient['last_name'] || recipient['recipient_last_name'] || '';
    const recipientFullName = [recipientFirstName, recipientLastName].filter(part => String(part).trim()).join(' ').trim();
    const recipientEmail = recipient['recipient_email'] || recipient['email'] || '';
    const recipientUniqueId = buildRecipientUniqueId(recipient, index);
    const assessment = calculateNutritionAssessment(recipientUniqueId);
    nutritionalAssessment.push({
      recipient_name: recipientFullName,
      recipient_email: recipientEmail,
      nutrition_risk_questions: assessment?.nutrition_risk_questions || [],
      total_score: assessment?.total_score ?? 0,
      risk_level: assessment?.risk_level || ''
    });
  });

  // Build income details from senior center income list table
  // Same amount/date pair mapping as UA/WAP: 1070/1071 → amount1/date1, 1070_1/1071_1 → amount2/date2, …
  const incomeDetails: any[] = [];
  const incomeTableData = formValues['table_income_list_table'] || [];
  incomeTableData.forEach((income: any) => {
    const incomeEntry: any = {
      email: income['email'] || '',
      recipient_name: income['recipient_name'] || income['1093'] || '',
      recipient_email: income['recipient_email'] || '',
      income_type: income['income_type'] || income['1068'] || '',
      income_source: income['income_source'] || '',
      is_family: income['is_family'] !== undefined ? income['is_family'] : true,
      family_email: income['family_email'] || income['email'] || '',
      frequency: income['1069'] || income['frequency'] || '',
      income_document: income['income_document'] || [],
      upload_key: income['upload_key'] || []
    };

    const incomePairs: Array<{ amount: any; date: any }> = [];
    if (income['1070'] || income['1071']) {
      incomePairs.push({
        amount: income['1070'] || '',
        date: income['1071'] || '',
      });
    }
    for (let i = 1; i <= 5; i++) {
      const amountKey = `1070_${i}`;
      const dateKey = `1071_${i}`;
      if (income[amountKey] || income[dateKey]) {
        incomePairs.push({
          amount: income[amountKey] || '',
          date: income[dateKey] || '',
        });
      }
    }
    incomePairs.forEach((pair, index) => {
      const pairNumber = index + 1;
      if (pairNumber <= 5) {
        incomeEntry[`income_amount${pairNumber}`] = pair.amount || '';
        incomeEntry[`income_date${pairNumber}`] = pair.date ? formatDate(pair.date) : '';
      }
    });
    // Keep legacy single amount for older consumers / display fallbacks
    if (incomePairs.length > 0) {
      incomeEntry.income_amount = incomePairs[0].amount || '';
    }

    incomeDetails.push(incomeEntry);
  });

  // Build client preferences (per recipient)
  const clientPreferences: any = { recipients: [] };
  const mealType = formValues['meal_type']
    ? (Array.isArray(formValues['meal_type']) ? formValues['meal_type'] : [formValues['meal_type']])
    : [];
  const drinkType = formValues['drink_type']
    ? (Array.isArray(formValues['drink_type']) ? formValues['drink_type'] : [formValues['drink_type']])
    : [];
  const activities = formValues['activities']
    ? (Array.isArray(formValues['activities']) ? formValues['activities'] : [formValues['activities']])
    : [];
  const activitiesSpecifyOther = getSpecifyOtherValue(
    activities,
    formValues['activities_specify_other']
  );
  const transportationOptionRaw = formValues['transportation_option'];
  const transportationOption =
    transportationOptionRaw != null && String(transportationOptionRaw).trim() !== ''
      ? String(
          typeof transportationOptionRaw === 'object' &&
            transportationOptionRaw !== null &&
            'value' in transportationOptionRaw
            ? (transportationOptionRaw as { value?: unknown }).value
            : transportationOptionRaw
        ).trim()
      : '';
  recipients.forEach((recipient: any) => {
    clientPreferences.recipients.push({
      recipient_name: recipient.recipient_name || [recipient.first_name, recipient.last_name].filter(Boolean).join(' ').trim(),
      recipient_email: recipient.recipient_email || recipient.email || '',
      meal_type: mealType,
      drink_type: drinkType,
      activities: activities,
      activities_specify_other: activitiesSpecifyOther || undefined,
      ...(transportationOption ? { transportation_option: transportationOption } : {})
    });
  });

  // Build contact information
  const contactInformation: any = {};
  if (formValues['contact_information_phone'] || formValues['contact_phone'] || formValues['1006']) {
    contactInformation.phone = formValues['contact_information_phone'] || formValues['contact_phone'] || formValues['1006'];
  }
  if (formValues['contact_information_email'] || formValues['contact_email_address'] || formValues['1005']) {
    contactInformation.email_address = formValues['contact_information_email'] || formValues['contact_email_address'] || formValues['1005'];
  }
  if (formValues['lives_alone']) {
    contactInformation.lives_alone = formValues['lives_alone'];
  }
  if (formValues['lives_with']) {
    contactInformation.lives_with = formValues['lives_with'];
  }
  if (formValues['lives_with']) {
    const livesWithSpecifyOther = getSpecifyOtherValue(
      formValues['lives_with'],
      formValues['lives_with_specify_other']
    );
    if (livesWithSpecifyOther) {
      contactInformation.lives_with_specify_other = livesWithSpecifyOther;
    }
  }
  if (formValues['place_of_residence']) {
    contactInformation.place_of_residence = formValues['place_of_residence'];
  }
  if (formValues['place_of_residence']) {
    const placeSpecifyOther = getSpecifyOtherValue(
      formValues['place_of_residence'],
      formValues['place_of_residence_specify_other']
    );
    if (placeSpecifyOther) {
      contactInformation.place_of_residence_specify_other = placeSpecifyOther;
    }
  }
  if (formValues['number_of_family_members']) {
    contactInformation.number_of_family_members = String(formValues['number_of_family_members']);
  }
  if (formValues['approval_location_id']) {
    contactInformation.approval_location_id = String(formValues['approval_location_id']);
  }
  if (formValues['approval_location_name']) {
    contactInformation.approval_location_name = String(formValues['approval_location_name']);
  }

  // Application details
  const applicationDetails = {
    household_type: formValues['1022'] || '',
    own_or_rent_address: formValues['1023'] || '',
    pays_rent: formValues['1001'] || '',
    how_much: formValues['1118'] || [],
    payment_frequency: formValues['1114'] || '',
    apartment_or_landlord_name: formValues['1115'] || '',
    landlord_telephone: formValues['1116'] || '',
    landlord_alternate_telephone: formValues['1117'] || '',
    landlord_email: formValues['1119'] || '',
    service_utility: formValues['1024'] || '',
    cool_home: formValues['1025'] || '',
    heat_home: formValues['1026'] || ''
  };

  // Handle applicant_sign field (field 1058)
  let applicantSign = '';
  const firstName = formValues['1058_first'] || '';
  const lastName = formValues['1058_last'] || '';
  const initial = formValues['1058_initial'] || '';
  
  if (firstName || lastName || initial) {
    const parts = [firstName, lastName, initial].filter(part => part && part.trim().length > 0);
    applicantSign = parts.join(' ').trim();
  } else {
    applicantSign = formValues['1058'] || '';
  }

  // Declaration and acknowledgements
  const isDeclarationAccepted = isCheckboxAccepted(formValues['1057']);
  const isReleaseOfInformationAccepted = isCheckboxAccepted(formValues['1099']);
  const isApplicantAcknowledgementAccepted = isCheckboxAccepted(formValues['1110']);

  const householderEmail = pickStr('contact_information_email', '1005', 'email_address').trim();
  const primaryContactEmail =
    pickStr('primary_contact_email', '1067') || householderEmail;

  // Normalize upload key values to arrays
  const normalizeStringArray = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.filter(v => v && typeof v === 'string');
    }
    if (typeof value === 'string') {
      return value.split(',').map(v => v.trim()).filter(Boolean);
    }
    return [];
  };

  const composeSignatureFromParts = (baseFieldId: string): string => {
    const fullName = String(formValues[baseFieldId] || '').trim();
    if (fullName) {
      return fullName;
    }
    const firstName = String(formValues[`${baseFieldId}_first`] || '').trim();
    const lastName = String(formValues[`${baseFieldId}_last`] || '').trim();
    const initial = String(formValues[`${baseFieldId}_initial`] || '').trim();
    return [firstName, lastName, initial].filter(Boolean).join(' ').trim();
  };

  // Ensure contact information includes primary contact email
  contactInformation.primary_contact_email = primaryContactEmail || '';

  const recipientReferences = recipients.map((recipient: any) => ({
    recipient_name: recipient.recipient_name || [recipient.first_name, recipient.last_name].filter(Boolean).join(' ').trim(),
    recipient_email: recipient.recipient_email || recipient.email || ''
  }));

  const seniorCenterProofType = String(formValues['client_rights_identification_proof_type'] || '').trim();
  const seniorCenterProofUploadKeys = normalizeStringArray(formValues['client_rights_identification_proof_file']).slice(0, 1);
  const todayDate = formatDate(new Date());

  const clientRightsResponsibilities = {
    recipients: recipientReferences,
    referral_source_type: formValues['client_rights_referral_source_type'] || formValues['referral_source_type'] || '',
    referral_source_type_specify_other:
      formValues['client_rights_referral_source_type_specify_other'] || formValues['referral_source_type_specify_other'] || '',
    identification_proof: seniorCenterProofType ? [seniorCenterProofType] : ([] as string[]),
    identification_proof_type: seniorCenterProofType,
    identification_proof_document: seniorCenterProofUploadKeys,
    acknowledged_rights_responsibilities: isCheckboxAccepted(formValues['client_rights_declaration']),
    acknowledgement_date:
      formatDate(formValues['client_rights_acknowledgement_date']) || todayDate,
    client_signature: composeSignatureFromParts('client_rights_signature')
  };

  const codeOfConductMethod = String(formValues['code_of_conduct_acknowledgement_method'] || '').trim();
  const codeOfConduct = {
    recipients: recipientReferences,
    acknowledged_rules: codeOfConductMethod === 'read_self',
    rules_read_to_me_acknowledged: codeOfConductMethod === 'read_to_me',
    acknowledgement_date:
      formatDate(formValues['code_of_conduct_acknowledgement_date']) || todayDate,
    client_signature: composeSignatureFromParts('code_of_conduct_signature')
  };

  const rawPhotoReleaseMethod = formValues['photo_release_acknowledgement_method'];
  const photoReleaseMethod =
    rawPhotoReleaseMethod !== null &&
    typeof rawPhotoReleaseMethod === 'object' &&
    'value' in rawPhotoReleaseMethod
      ? String((rawPhotoReleaseMethod as { value?: unknown }).value ?? '').trim()
      : String(rawPhotoReleaseMethod ?? '').trim();
  const photoRelease = {
    recipients: recipientReferences,
    acknowledged_photo_release: photoReleaseMethod === 'acknowledge',
    refused_to_sign: photoReleaseMethod === 'refused',
    acknowledgement_date:
      formatDate(formValues['photo_release_acknowledgement_date']) || todayDate,
    client_signature: composeSignatureFromParts('photo_release_signature')
  };

  const physicalActivityWaiver = {
    recipients: recipientReferences,
    acknowledged_physical_activity_waiver: isCheckboxAccepted(formValues['physical_activity_declaration']),
    acknowledgement_date:
      formatDate(formValues['physical_activity_acknowledgement_date']) || todayDate,
    client_signature: composeSignatureFromParts('physical_activity_signature')
  };

  const fieldTripAgreement = {
    recipients: recipientReferences,
    acknowledged_field_trip_agreement: isCheckboxAccepted(formValues['field_trip_declaration']),
    acknowledgement_date:
      formatDate(formValues['field_trip_acknowledgement_date']) || todayDate,
    client_signature: composeSignatureFromParts('field_trip_signature')
  };

  const signedAgreements = {
    documents: normalizeStringArray(formValues['signed_agreements_files']).slice(0, 1)
  };

  // Build final payload (support legacy numeric field_ids and current semantic ids from senior-center-program.json)
  const genderValue = pickStr('1015', 'gender');
  const raceValue = pickStr('1016', 'race');
  const communicationPreferenceValue = pickStr('communication_preference');
  const genderSpecifyOther = getSpecifyOtherValue(genderValue, formValues['gender_specify_other']);
  const raceSpecifyOther = getSpecifyOtherValue(raceValue, formValues['race_specify_other']);
  const communicationPreferenceSpecifyOther = getSpecifyOtherValue(
    communicationPreferenceValue,
    formValues['communication_preference_specify_other']
  );
  const payload: SeniorCenterProgramPayload = {
    program_id: programId,
    referral_source_type: formValues['referral_source_type'] || '',
    referral_source_type_specify_other: formValues['referral_source_type_specify_other'] || '',
    household_name: pickStr('household_name', '1000'),
    primary_contact_email: primaryContactEmail,
    identity_upload_key: normalizeStringArray(formValues['identity_upload_key']),
    citizenship_upload_key: normalizeStringArray(formValues['citizenship_upload_key']),
    proof_of_identity: formValues['proof_of_identity'] || '',
    proof_of_citizenship: formValues['proof_of_citizenship'] || '',
    // Prospect Contact Information
    first_name: pickStr('1012', 'first_name'),
    last_name: pickStr('1013', 'last_name'),
    dob: formatDate(pickRaw('1014', 'date_of_birth')),
    gender: genderValue,
    gender_specify_other: genderSpecifyOther || undefined,
    race: raceValue,
    race_specify_other: raceSpecifyOther || undefined,
    ethnicity: pickStr('1017', 'ethnicity'),
    military_status: pickStr('1018', 'military_status', 'veteran'),
    marital_status: pickStr('marital_status'),
    communication_preference: communicationPreferenceValue,
    communication_preference_specify_other: communicationPreferenceSpecifyOther || undefined,
    is_migrant_farm_worker: pickStr('1019', 'is_migrant_farm_worker'),
    is_disabled: pickStr('1004', 'is_disabled'),
    citizenship: pickStr('1020', 'citizenship'),
    is_receiving_income: toYesNo(pickRaw('1021', 'is_receiving_income') ?? '') || '',
    // Contact Information
    contact_information: {
      phone: contactInformation.phone || '',
      email_address: contactInformation.email_address || '',
      primary_contact_email: primaryContactEmail || '',
      lives_alone: contactInformation.lives_alone || '',
      lives_with: contactInformation.lives_with || '',
      lives_with_specify_other: contactInformation.lives_with_specify_other || '',
      place_of_residence: contactInformation.place_of_residence || '',
      place_of_residence_specify_other: contactInformation.place_of_residence_specify_other || '',
      number_of_family_members: contactInformation.number_of_family_members || '',
      approval_location_id: contactInformation.approval_location_id || '',
      approval_location_name: contactInformation.approval_location_name || ''
    },
    // Address Information
    address: addresses,
    // Recipients
    recipients: recipients,
    // Emergency Contacts
    emergency_contacts: emergencyContacts,
    // Health Plans
    health_plans: healthPlans,
    // Nutritional Assessment
    nutritional_assessment: nutritionalAssessment,
    // Monthly Household Income Information
    total_household_members: formValues['total_household_members'] ? String(formValues['total_household_members']) : '',
    total_household_income:
      formValues['total_household_income'] !== undefined &&
      formValues['total_household_income'] !== null &&
      String(formValues['total_household_income']).trim() !== ''
        ? String(formValues['total_household_income'])
        : null,
    has_additional_monthly_household_income:
      formValues['has_additional_monthly_household_income'] !== undefined &&
      formValues['has_additional_monthly_household_income'] !== null &&
      String(formValues['has_additional_monthly_household_income']).trim() !== ''
        ? String(formValues['has_additional_monthly_household_income']).trim()
        : '',
    total_other_monthly_household_income:
      formValues['total_other_monthly_household_income'] !== undefined &&
      formValues['total_other_monthly_household_income'] !== null &&
      String(formValues['total_other_monthly_household_income']).trim() !== ''
        ? String(formValues['total_other_monthly_household_income']).trim()
        : '',
    // Income Details
    income_details: incomeDetails,
    // Client Preferences
    client_preferences: clientPreferences,
    // Top-level copy for list/transport queries that read form_json.transportation_option
    ...(transportationOption ? { transportation_option: transportationOption } : {}),
    // Waiver sections
    client_rights_responsibilities: clientRightsResponsibilities,
    code_of_conduct: codeOfConduct,
    photo_release: photoRelease,
    physical_activity_waiver: physicalActivityWaiver,
    field_trip_agreement: fieldTripAgreement,
    signed_agreements: signedAgreements,
    // Application Details
    application_details: applicationDetails,
    // Service Details (empty for Senior Center Program)
    service_details: [],
    // Weatherization Details (null for Senior Center Program)
    weatherization_details: null,
    // Signatures and Declarations
    applicant_sign: applicantSign,
    is_declaration_accepted: isDeclarationAccepted,
    applicant_acknowledgement: isApplicantAcknowledgementAccepted || null,
    release_of_information: isReleaseOfInformationAccepted,
    // Status
    status: status
  };

  omitEmptyStringLeaves(payload as any);
  return payload;
}



